import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { formatDateOnly, toDateOnlyString } from "../lib/dateOnly";
import { startCardPrefetch, awaitCardPrefetch, clearCardPrefetch } from "../lib/cardPrefetch";
import { mergeDetail, refreshFromRow } from "../lib/intakeStatusMerge";
import { Icd10Panel } from "../components/Icd10Panel";
import { DocumentViewerPanel, isFormFieldTarget } from "../components/DocumentViewerPanel";
import {
  FileCheck,
  AlertCircle,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Check,
  X,
  Building2,
  Briefcase,
  MapPin,
  Car,
  HardHat,
  Loader2,
  Search,
  RefreshCw,
  ClipboardCheck,
  ArrowRight,
  FolderOpen,
  Ban,
  XCircle,
  CheckCircle2,
  Edit2,
  AlertTriangle,
  Eye,
  EyeOff,
  Lock,
  FileText,
  Clock,
  StickyNote,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Date-of-birth formatter — em-dash for null, MM/DD/YYYY otherwise.
// 2026-08-20 hotfix: delegates to the shared `formatDateOnly` in
// lib/dateOnly.js, which formats via pure string reordering (no
// `new Date("YYYY-MM-DD")` anywhere) so a stored DATE never shifts
// −1 on render in Pacific.
function formatDOB(dateValue) {
  return formatDateOnly(dateValue, { nullLabel: "—" });
}


// Cancellation reason options shown in the dropdown
const CANCEL_REASONS = [
  { value: "service_not_offered", label: "Service not offered" },
  { value: "duplicate",           label: "Duplicate referral"  },
  { value: "patient_request",     label: "Patient request"     },
  { value: "other",               label: "Other"               },
];
const CANCEL_REASON_LABEL = Object.fromEntries(CANCEL_REASONS.map((r) => [r.value, r.label]));

// Format any FastAPI/Pydantic error into a renderable string
// Handles 422 array of objects, plain string detail, and unknown shapes
// Mirrors backend crm_router.INTAKE_TYPE_VALUES (verbatim, en-dash in the fourth).
const INTAKE_TYPE_OPTIONS = ["New", "Continuing", "Readmit", "Returning Patient – New Case"];

// Mirrors backend crm_router.LOCK_TTL_MINUTES (do not change independently).
const LOCK_TTL_MINUTES = 5;

function getIntakeSessionId() {
  let id = sessionStorage.getItem("intake_session_id");
  if (!id) {
    id = `intake-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem("intake_session_id", id);
  }
  return id;
}

function formatRelativeTime(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "1 day ago" : `${days} days ago`;
}

function formatApiError(err, fallback = "Request failed") {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => (typeof d === "string" ? d : (d?.msg || JSON.stringify(d))))
      .join("; ");
  }
  if (typeof detail === "object") return detail.msg || detail.message || JSON.stringify(detail);
  return String(detail);
}

// Helper to get token from session or local storage
const getStoredToken = () => {
  return sessionStorage.getItem("token") || localStorage.getItem("token");
};

// Status badge component
// WestStar/Network row badge (2026-06) — value comes ONLY from the
// server-side `location_class` field (single classification source in
// services/location_classifier; mirrors LOCATION_CLASS_STYLES in CRMQueue).
const LOCATION_CLASS_STYLES = {
  weststar:   { chip: "bg-indigo-100 text-indigo-700 border border-indigo-200", dot: "bg-indigo-500", label: "WestStar" },
  network:    { chip: "bg-teal-100 text-teal-700 border border-teal-200",       dot: "bg-teal-500",   label: "Network" },
  unassigned: { chip: "bg-slate-100 text-slate-500 border border-slate-200",    dot: "bg-slate-400",  label: "Unassigned" },
};

function LocationClassBadge({ cls, testId }) {
  const s = LOCATION_CLASS_STYLES[cls] || LOCATION_CLASS_STYLES.unassigned;
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${s.chip}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

function StatusBadge({ complete, label }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
        complete
          ? "bg-emerald-100 text-emerald-700"
          : "bg-amber-100 text-amber-700"
      }`}
    >
      {complete ? <Check size={12} /> : <AlertCircle size={12} />}
      {label}
    </span>
  );
}

// Task checklist item
// Presentation-only wrapper (2026-06 iteration): in INCOMPLETE context the
// three task blocks tuck behind a small expandable so the Required
// Information panel and the patient cards share the viewport — same
// children, same wiring, zero functional change. Everywhere else the tasks
// render exactly as before.
function TasksWrap({ collapse, children }) {
  if (!collapse) return <>{children}</>;
  return (
    <details data-testid="intake-tasks-details">
      <summary
        className="text-xs font-medium text-blue-700 cursor-pointer select-none py-1"
        data-testid="intake-tasks-summary"
      >
        Intake checklist tasks (billing / payor / transfer)
      </summary>
      <div className="mt-2 space-y-2">{children}</div>
    </details>
  );
}

function TaskItem({ title, description, complete, onClick, disabled, children }) {
  return (
    <div
      className={`p-4 rounded-lg border transition-all ${
        complete
          ? "bg-emerald-50 border-emerald-200"
          : "bg-white border-slate-200 hover:border-blue-300"
      } ${disabled ? "opacity-50" : ""}`}
    >
      <div className="flex items-start gap-3">
        <button
          onClick={onClick}
          disabled={disabled}
          className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
            complete
              ? "bg-emerald-500 border-emerald-500"
              : "border-slate-300 hover:border-blue-500"
          }`}
        >
          {complete && <Check size={12} className="text-white" />}
        </button>
        <div className="flex-1">
          <p className={`font-medium ${complete ? "text-emerald-700" : "text-slate-900"}`}>
            {title}
          </p>
          <p className="text-sm text-slate-500 mt-0.5">{description}</p>
          {children && <div className="mt-3">{children}</div>}
        </div>
      </div>
    </div>
  );
}

// Inline-editable read/write field used in the referral detail panel.
// - Hover reveals the edit pencil; click → input replaces the value.
// - Enter saves, Esc cancels, blur saves (mirrors CRM Queue UX).
// - Saving is delegated to the parent via `onSave(fieldName, newValue)` so
//   each panel decides whether the write goes to BQ, PG, or both.
// - Date type renders a native picker and emits YYYY-MM-DD strings.
function EditableField({ label, value, fieldName, onSave, canEdit = true, type = "text", placeholder = "", suffix = null, options = null, missing = false }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value ?? "");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) inputRef.current.focus();
  }, [isEditing]);

  // Keep editValue in sync with prop changes while NOT editing. Updating
  // during an active edit would clobber the user's typing if the parent
  // refreshes (e.g. via the 5-sec poll) mid-edit.
  useEffect(() => {
    if (!isEditing) setEditValue(value ?? "");
  }, [value, isEditing]);

  // Missing required value: muted + italic "not found" copy (accordion
  // restyle) — presentation only, save/edit behavior identical.
  // 2026-08-20 hotfix: date-only fields render through `formatDateOnly`
  // so a stored 'YYYY-MM-DD' never renders one day early in Pacific
  // (the old `value || —` pass-through printed the raw ISO string,
  // and some BQ shapes returned full timestamps that would have been
  // rendered ugly-or-shifted downstream).
  const rendered = type === "date" && value ? formatDateOnly(value) : value;
  const displayValue = rendered || (
    <span className={missing ? "text-slate-400 italic font-normal" : "text-slate-400"}>
      {missing ? "— not found —" : "—"}
    </span>
  );

  const handleSave = async () => {
    // 2026-08-20 hotfix: for date-only fields, force the outgoing
    // payload to a bare YYYY-MM-DD string. `toDateOnlyString` never
    // calls `.toISOString()`, so an evening edit can't drift +1 via
    // UTC conversion of a picked-date-plus-current-time.
    const raw = editValue === "" ? null : editValue;
    const newVal = type === "date" ? toDateOnlyString(raw) : raw;
    const oldVal = type === "date" ? toDateOnlyString(value) : (value || null);
    if (newVal === oldVal) {
      setIsEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(fieldName, newVal);
      setIsEditing(false);
    } catch (err) {
      // onSave is responsible for surfacing the error toast/alert
      console.error("Save failed:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditValue(value ?? "");
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSave();
    if (e.key === "Escape") handleCancel();
  };

  if (!canEdit) {
    return (
      <div>
        <p className="text-xs text-slate-500">{label}</p>
        <p className="font-medium text-slate-900 break-words">
          {displayValue}
          {suffix && <span className="font-normal text-slate-500" data-testid={`editable-${fieldName}-suffix`}> – {suffix}</span>}
        </p>
      </div>
    );
  }

  return (
    <div className="group" data-testid={`editable-${fieldName}`}>
      <p className="text-xs text-slate-500">{label}</p>
      {isEditing ? (
        <div className="flex items-center gap-1 mt-0.5">
          {options ? (
            <select
              ref={inputRef}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={saving}
              className="flex-1 min-w-0 text-sm px-2 py-1 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              data-testid={`editable-${fieldName}-select`}
            >
              {!options.includes(editValue) && <option value="" disabled>Select…</option>}
              {options.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          ) : (
            <input
              ref={inputRef}
              type={type === "date" ? "date" : "text"}
              value={type === "date" && editValue ? String(editValue).slice(0, 10) : editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleSave}
              placeholder={placeholder}
              disabled={saving}
              className="flex-1 min-w-0 text-sm px-2 py-1 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid={`editable-${fieldName}-input`}
            />
          )}
          {saving ? (
            <Loader2 size={14} className="animate-spin text-slate-400" />
          ) : (
            <>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleSave}
                className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
                title="Save"
                data-testid={`editable-${fieldName}-save`}
              >
                <Check size={14} />
              </button>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleCancel}
                className="p-1 text-rose-500 hover:bg-rose-50 rounded"
                title="Cancel"
                data-testid={`editable-${fieldName}-cancel`}
              >
                <XCircle size={14} />
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-1.5">
          <p className="font-medium text-slate-900 break-words">
            {displayValue}
            {suffix && <span className="font-normal text-slate-500" data-testid={`editable-${fieldName}-suffix`}> – {suffix}</span>}
          </p>
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            className="p-0.5 text-slate-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"
            title={`Edit ${label}`}
            data-testid={`editable-${fieldName}-edit`}
          >
            <Edit2 size={12} />
          </button>
        </div>
      )}
    </div>
  );
}


// ── SSN field ────────────────────────────────────────────────────────────────
// Masked display driven by `patient_ssn_state` / `patient_ssn_last4` — the raw
// SSN never arrives in the referral payload. Three states:
//   'none'    → normal empty editable field (NULL/malformed)
//   'unknown' → the literal sentinel 999-99-9999, muted, "(not provided)" —
//               staff recognize it as the "no SSN available" convention.
//               No mask/Reveal; Edit available (input opens empty; saving
//               empty is a no-op so the sentinel is preserved, never NULLed).
//   'on_file' → masked •••-••-{last4} + audited Reveal (30s auto-remask).
// The full value never lingers in component state beyond the reveal window.
function SsnField({ fileId, ssnState, last4, onSave, canEdit }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [revealed, setRevealed] = useState(null);
  const [revealing, setRevealing] = useState(false);
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  const hide = useCallback(() => {
    setRevealed(null);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    // Re-mask + reset when switching referrals; clear the timer on unmount.
    hide();
    setIsEditing(false);
    setEditValue("");
    return hide;
  }, [fileId, hide]);

  useEffect(() => {
    if (isEditing && inputRef.current) inputRef.current.focus();
  }, [isEditing]);

  const handleReveal = async () => {
    if (revealed) {
      hide();
      return;
    }
    setRevealing(true);
    try {
      const res = await axios.get(`${API}/api/intake/referrals/${fileId}/ssn`, {
        headers: { Authorization: `Bearer ${getStoredToken()}` },
      });
      setRevealed(res.data.ssn);
      timerRef.current = setTimeout(hide, 30000); // auto-remask after 30s
    } catch (err) {
      console.error("SSN reveal failed"); // never log the value
      alert(formatApiError(err, "Failed to reveal SSN"));
    } finally {
      setRevealing(false);
    }
  };

  const handleSave = async () => {
    const trimmed = editValue.trim();
    if (trimmed === "" && ssnState !== "on_file") {
      // No-op: preserves the sentinel ('unknown') and NULL ('none') alike —
      // never silently convert the sentinel to NULL.
      handleCancel();
      return;
    }
    setSaving(true);
    try {
      await onSave("patient_ssn", trimmed === "" ? null : trimmed);
      setIsEditing(false);
      setEditValue(""); // never keep the full value in state after save
      hide();
    } catch (err) {
      // onSave surfaces the error alert
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditValue("");
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSave();
    if (e.key === "Escape") handleCancel();
  };

  const masked = ssnState === "on_file" && last4 ? `•••-••-${last4}` : null;
  const isUnknown = ssnState === "unknown";

  return (
    <div className="group" data-testid="ssn-field">
      <p className="text-xs text-slate-500">Social Security Number</p>
      {isEditing ? (
        <div className="flex items-center gap-1 mt-0.5">
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value.replace(/[^\d-]/g, ""))}
            onKeyDown={handleKeyDown}
            placeholder="###-##-#### (9 digits)"
            maxLength={11}
            disabled={saving}
            className="flex-1 min-w-0 text-sm px-2 py-1 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="ssn-input"
          />
          {saving ? (
            <Loader2 size={14} className="animate-spin text-slate-400" />
          ) : (
            <>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleSave}
                className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
                title="Save"
                data-testid="ssn-save"
              >
                <Check size={14} />
              </button>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleCancel}
                className="p-1 text-rose-500 hover:bg-rose-50 rounded"
                title="Cancel"
                data-testid="ssn-cancel"
              >
                <XCircle size={14} />
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-1.5">
          {isUnknown ? (
            <p className="font-medium text-slate-500" data-testid="ssn-display">
              999-99-9999 <span className="text-xs font-normal text-slate-400">(not provided)</span>
            </p>
          ) : (
            <p className="font-medium text-slate-900" data-testid="ssn-display">
              {revealed || masked || <span className="text-slate-400">—</span>}
            </p>
          )}
          {masked && (
            <button
              type="button"
              onClick={handleReveal}
              disabled={revealing}
              className="p-0.5 text-slate-400 hover:text-blue-600"
              title={revealed ? "Hide SSN" : "Reveal SSN (audited)"}
              data-testid="ssn-reveal-btn"
            >
              {revealing ? (
                <Loader2 size={12} className="animate-spin" />
              ) : revealed ? (
                <EyeOff size={12} />
              ) : (
                <Eye size={12} />
              )}
            </button>
          )}
          {canEdit && (
            <button
              type="button"
              onClick={() => {
                hide();
                setIsEditing(true);
              }}
              className="p-0.5 text-slate-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"
              title="Edit Social Security Number"
              data-testid="ssn-edit-btn"
            >
              <Edit2 size={12} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}


// Confirmation modal for cancelling a referral.
// `reasons` defaults to CANCEL_REASONS so existing Pending-tab callers stay
// identical. The Incomplete tab passes its own list. `title`/`confirmLabel`
// let the modal serve double-duty without forking it.
function CancelReferralModal({
  open, onClose, onConfirm, busy, patientName,
  reasons,
  title = "Cancel Referral",
  confirmLabel = "Cancel Referral",
  description,
}) {
  const reasonList = reasons || CANCEL_REASONS;
  const [reason, setReason] = useState(reasonList[0]?.value || "");
  const [notes, setNotes] = useState("");

  // Reset whenever the modal is reopened
  useEffect(() => {
    if (open) {
      setReason(reasonList[0]?.value || "");
      setNotes("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="cancel-referral-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
          <Ban size={18} className="text-rose-600" />
          <h3 className="font-bold text-slate-900">{title}</h3>
        </div>

        <div className="p-5 space-y-4">
          <div className="text-sm text-slate-600">
            {description ? (
              description
            ) : (
              <>
                Are you sure you want to cancel the referral for{" "}
                <span className="font-medium text-slate-900">{patientName}</span>?{" "}
                <span className="text-slate-700">This cannot be undone.</span>
              </>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Cancellation reason
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white"
              data-testid="cancel-reason-select"
            >
              {reasonList.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any context for the audit log…"
              rows={3}
              className="w-full p-3 rounded-lg border border-slate-200 text-sm resize-none"
              data-testid="cancel-notes-input"
            />
          </div>
        </div>

        <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200 rounded-lg disabled:opacity-50"
            data-testid="cancel-modal-keep"
          >
            Keep referral
          </button>
          <button
            onClick={() => onConfirm({ reason, notes: notes.trim() || null })}
            disabled={busy}
            className="px-4 py-2 text-sm font-medium text-white bg-rose-600 hover:bg-rose-700 rounded-lg disabled:opacity-50 flex items-center gap-2"
            data-testid="cancel-modal-confirm"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />}
            Cancel referral
          </button>
        </div>
      </div>
    </div>
  );
}

// Reasons accepted by /api/intake/incomplete/{id}/move-to-cancelled. Kept
// separate from CANCEL_REASONS so the Pending-tab cancel dropdown is
// untouched.
const INCOMPLETE_CANCEL_REASONS = [
  { value: "duplicate",        label: "Duplicate referral" },
  { value: "patient_declined", label: "Patient declined" },
  { value: "invalid",          label: "Invalid referral" },
  { value: "missing_info",     label: "Missing critical information" },
  { value: "other",            label: "Other" },
];

// Mirrors backend INCOMPLETE_REASONS in intake_router.py — keep verbatim.
// Drives the Move-to-Incomplete reason dropdown, the Incomplete-tab reason
// chip vocabulary, and the "Incomplete Reason" EditableField options.
const INCOMPLETE_REASON_OPTIONS = [
  "Missing Rx",
  "Expired Rx",
  "Missing Diagnosis/Body Parts",
  "Other",
];

// Mirrors backend THERAPY_TYPE_VALUES in intake_router.py — keep verbatim.
// Occupational Therapy deliberately excluded (not a WestStar service).
const THERAPY_TYPE_OPTIONS = [
  "Physical Therapy",
  "Acupuncture",
  "Aquatic Therapy",
];

// ── Accordion restyle (2026-06) — presentation-only helpers ────────────────
// Maps each /requirements field key to its accordion section. Shared with
// the summary banner + section chips so ALL missing derivation flows from
// the ONE existing requirements payload (never a parallel rules engine).
const REQ_KEY_SECTION = {
  case_type: "referral",
  date_of_injury: "referral",
  date_of_accident: "referral",
  claim_number: "referral",
  body_parts_involved: "referral",
  primary_physician_name: "referral",
  therapy_confirmation: "referral",
  patient_name: "patient",
  date_of_birth: "patient",
  patient_home_phone: "patient",
  patient_address: "patient",
  employer_name: "payor",
  insurance_carrier: "payor",
  attorney_name: "payor",
  attorney_phone: "payor",
};
// Requirement keys already rendered as static referral.* editors in the
// accordion bodies — requirements-only extras skip these to avoid dupes.
const STATIC_FIELD_KEYS = new Set([
  "case_type", "date_of_birth", "date_of_injury", "date_of_accident",
  "claim_number", "insurance_carrier", "employer_name", "body_parts_involved",
]);

// Small amber tag + slot wrapper for a required field that is missing.
function ReqFieldSlot({ missing, fieldKey, className = "", children }) {
  return (
    <div className={className}>
      {missing && (
        <span
          className="float-right ml-1.5 text-[9px] font-bold uppercase tracking-wide text-amber-700 bg-amber-100 px-1.5 py-px rounded"
          data-testid={`req-missing-${fieldKey}`}
        >
          Missing
        </span>
      )}
      {children}
    </div>
  );
}

// One collapsible section of the detail-panel accordion. Chip renders only
// when the requirements payload exists (incomplete context).
function AccordionSection({ id, title, open, onToggle, missingCount = 0, showChip = false, children }) {
  return (
    <div className="border border-slate-200 rounded-lg bg-white" data-testid={`accordion-section-${id}`}>
      <button
        type="button"
        onClick={() => onToggle(id)}
        className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left hover:bg-slate-50 rounded-lg transition-colors"
        data-testid={`accordion-toggle-${id}`}
        aria-expanded={open}
      >
        <span className="text-sm font-bold text-slate-900">{title}</span>
        <span className="flex items-center gap-2 flex-shrink-0">
          {showChip && (missingCount > 0 ? (
            <span className="text-[10px] font-bold uppercase text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded" data-testid={`section-chip-${id}`}>
              {missingCount} missing
            </span>
          ) : (
            <span className="text-[10px] font-bold uppercase text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded" data-testid={`section-chip-${id}`}>
              Complete
            </span>
          ))}
          {open ? (
            <ChevronDown size={14} className="text-slate-500" />
          ) : (
            <ChevronRight size={14} className="text-slate-500" />
          )}
        </span>
      </button>
      {open && <div className="px-3 pb-2.5 pt-2 border-t border-slate-100">{children}</div>}
    </div>
  );
}

// Assigned Clinic TRANSFER picker (2026-06) — one setter, two screens.
// The value is the roster's `location_code`; the backend derives the label
// and moves the worklist fence with it, through the same canonical setter
// the CRM patient card's dropdown calls. Before this, the screen wrote
// display labels only: 467 mis-assignments, and after the convergence
// deploy the edits reverted within a sync cycle. No "clear" affordance —
// a NULL fence hides the patient from every worklist.
const _normTherapy = (t) => {
  const s = (t || "").trim().toLowerCase();
  return s === "aqua" ? "aquatic therapy" : s;
};

function ClinicSelectField({ name, city, options, therapyType, onSave, canEdit, note = null }) {
  const [editing, setEditing] = useState(false);
  const [filter, setFilter] = useState("");
  const [saving, setSaving] = useState(false);
  const displayLabel = name ? (city ? `${name} — ${city}` : name) : null;
  const q = filter.trim().toLowerCase();
  const filtered = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  // Therapy-aware GROUPING, never a hard filter: options matching the
  // referral's therapy (Aqua ↔ Aquatic Therapy mapped; roster-null therapy
  // always counts as matching) render first, the rest under Other clinics.
  const wantedSet = therapyType
    ? new Set(String(therapyType).split(",").map(_normTherapy).filter(Boolean))
    : null;
  const isMatch = (o) => !o.therapy_type || (wantedSet && wantedSet.has(_normTherapy(o.therapy_type)));
  const groups = wantedSet
    ? [
        { key: "matching", title: `Matching ${therapyType}`, items: filtered.filter(isMatch) },
        { key: "other", title: "Other clinics", items: filtered.filter((o) => !isMatch(o)) },
      ]
    : [{ key: "all", title: null, items: filtered }];
  const close = () => {
    setEditing(false);
    setFilter("");
  };
  // 2026-06: the value is the location_code. This field is a TRANSFER —
  // it moves the worklist fence with the label — so there is no "clear"
  // affordance any more: a NULL fence hides the patient from everyone.
  const pick = async (opt) => {
    setSaving(true);
    try {
      await onSave({ location_code: opt.location_code });
      close();
    } catch {
      /* handler already alerted */
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    // Legacy values not in the roster display AS-IS; the same edit
    // affordance opens the select.
    return (
      <div className="group" data-testid="assigned-clinic-field">
        <p className="text-xs text-slate-500">Assigned Clinic</p>
        <div className="flex items-start gap-1.5">
          <p className="font-medium text-slate-900 break-words">
            {displayLabel || <span className="text-slate-400">—</span>}
          </p>
          {canEdit && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="p-0.5 text-slate-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"
              title="Change clinic"
              data-testid="assigned-clinic-edit-btn"
            >
              <Edit2 size={12} />
            </button>
          )}
        </div>
        {note && (
          <p className="mt-1 text-[11px] text-amber-700" data-testid="assigned-clinic-note">
            {note}
          </p>
        )}
      </div>
    );
  }
  return (
    <div data-testid="assigned-clinic-field">
      <p className="text-xs text-slate-500 mb-1">Assigned Clinic</p>
      <div className="border border-slate-300 rounded-md bg-white shadow-sm" data-testid="assigned-clinic-select">
        {/* The save is seconds of BigQuery DML — staff read a silent,
            still-open picker as a dead click and re-tap (16 retry POSTs in
            one session). The in-flight state is explicit from click to
            close: a spinner+label here, and the options visually
            disabled below. */}
        {saving && (
          <div className="flex items-center gap-2 px-2 py-1.5 border-b border-slate-200 bg-slate-50 rounded-t-md"
               data-testid="assigned-clinic-saving">
            <Loader2 size={13} className="animate-spin text-blue-600" />
            <span className="text-xs text-slate-600">Saving clinic…</span>
          </div>
        )}
        <input
          autoFocus
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Escape") close(); }}
          placeholder="Type to filter clinics…"
          disabled={saving}
          className="w-full px-2 py-1.5 text-sm border-b border-slate-200 rounded-t-md focus:outline-none disabled:bg-slate-50 disabled:text-slate-400"
          data-testid="assigned-clinic-filter-input"
        />
        <div className={`max-h-52 overflow-y-auto py-1 transition-opacity ${saving ? "opacity-40 pointer-events-none" : ""}`}
             aria-busy={saving}>
          {groups.map((g) => g.items.length === 0 ? null : (
            <div key={g.key}>
              {g.title && (
                <p className="px-2 pt-1.5 pb-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-400" data-testid={`clinic-group-${g.key}`}>
                  {g.title}
                </p>
              )}
              {g.items.map((o) => (
                <button
                  key={o.location_code}
                  type="button"
                  onClick={() => pick(o)}
                  disabled={saving}
                  className={`w-full text-left px-2 py-1 text-sm hover:bg-slate-100 ${o.name === name && (o.city || null) === (city || null) ? "bg-slate-50 font-semibold" : ""}`}
                  data-testid={`clinic-option-${o.location_code}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          ))}
          {filtered.length === 0 && (
            <p className="px-2 py-1.5 text-xs text-slate-400" data-testid="clinic-no-match">No clinics match</p>
          )}
        </div>
        <button
          type="button"
          onClick={close}
          className="w-full px-2 py-1 text-[11px] text-slate-500 hover:bg-slate-50 border-t border-slate-200 rounded-b-md text-left"
          data-testid="assigned-clinic-cancel-btn"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// Raintree sync badge for the detail panel. Color encodes the high-level
// state; the tooltip carries detail (pending count, last outcome). The
// `unavailable` state means the BQ status query failed (e.g., upstream
// tables not yet provisioned) — grey it out instead of crashing.
function SyncStatusBadge({ status }) {
  if (!status) return null;
  const { portal_sync_status, last_edit_outcome, edits_pending } = status;
  let cls = "bg-slate-100 text-slate-600 border-slate-200";
  let label = "Sync: unknown";
  const failed =
    last_edit_outcome === "failed" ||
    (typeof last_edit_outcome === "string" && last_edit_outcome.includes("MAX_RETRIES_EXCEEDED"));

  if (failed) {
    cls = "bg-rose-100 text-rose-700 border-rose-200";
    label = "Sync failed";
  } else if (portal_sync_status === "in_sync" && (edits_pending || 0) === 0) {
    cls = "bg-emerald-100 text-emerald-700 border-emerald-200";
    label = "In sync";
  } else if (portal_sync_status === "pending_sync" || (edits_pending || 0) > 0) {
    cls = "bg-amber-100 text-amber-700 border-amber-200";
    label = edits_pending ? `Pending sync (${edits_pending})` : "Pending sync";
  } else if (portal_sync_status === "never_edited") {
    cls = "bg-slate-100 text-slate-600 border-slate-200";
    label = "Never edited";
  } else if (portal_sync_status === "unavailable") {
    cls = "bg-slate-100 text-slate-500 border-slate-200";
    label = "Sync: unavailable";
  }

  const tip = [
    `status: ${portal_sync_status || "unknown"}`,
    last_edit_outcome ? `last outcome: ${last_edit_outcome}` : null,
    edits_pending != null ? `unprocessed edits: ${edits_pending}` : null,
  ].filter(Boolean).join(" · ");

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${cls}`}
      title={tip}
      data-testid="sync-status-badge"
      data-sync-status={portal_sync_status || "unknown"}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${failed ? "bg-rose-500" : portal_sync_status === "in_sync" ? "bg-emerald-500" : portal_sync_status === "pending_sync" ? "bg-amber-500" : "bg-slate-400"}`} />
      {label}
    </span>
  );
}


// ICD-10 review/edit components (Icd10ReviewBadge, Icd10SearchBox,
// Icd10Panel) were extracted to `src/components/Icd10Panel.jsx` on
// 2026-06-11 so the CRM Queue could reuse them without duplicating any
// logic. Imported below; see that file for behaviour/permission notes.


// Referral detail panel
// Checkbox task → intake_status field it flips (optimistic overlay keys).
const TASK_STATUS_FIELD = {
  billing: "billing_complete",
  payor: "payor_complete",
  ready: "ready_for_crm",
};

function ReferralDetail({ referral, onUpdate, onClose, isIncompleteContext = false, isPendingContext = false, isPriorAuthContext = false, isCancelledContext = false, readOnly = false, lockedBy = null, taskOverlay = null, onTaskPending = () => {} }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [moveCancelOpen, setMoveCancelOpen] = useState(false);
  const [moveBusy, setMoveBusy] = useState(false);
  // Pending → Incomplete is a single-action override; no reason dropdown.
  // Tracked separately from `moveBusy` so the Incomplete-tab buttons don't
  // visually freeze when the Pending-tab button is in-flight (and vice
  // versa — the two contexts are mutually exclusive but kept independent
  // for clarity).
  const [moveToIncompleteBusy, setMoveToIncompleteBusy] = useState(false);
  // Cancelled → Pending is the reverse of Cancel Referral — single-action,
  // no modal needed. Separate busy flag so the Cancelled-tab button doesn't
  // share state with the Incomplete-tab one (an admin can technically have
  // both contexts open across page renders).
  const [unCancelBusy, setUnCancelBusy] = useState(false);
  // Custom modal instead of window.confirm — the preview environment's
  // iframe sandboxing auto-dismisses native confirm dialogs, which made the
  // earlier confirm-then-post path silently no-op. Cancel Referral already
  // uses an in-page modal for the same reason; this mirrors that pattern.
  const [moveToIncompleteOpen, setMoveToIncompleteOpen] = useState(false);
  // Required reason for Move to Incomplete (mirrors the cancel-reason
  // modal); reset whenever the modal opens fresh.
  const [moveToIncompleteReason, setMoveToIncompleteReason] = useState("");
  useEffect(() => {
    if (moveToIncompleteOpen) setMoveToIncompleteReason("");
  }, [moveToIncompleteOpen]);
  // Prior Auth moves — same single-action pattern as Pending → Incomplete
  // (confirm modal, no reason dropdown) with their own busy flags so these
  // buttons never share in-flight state with the other move families.
  const [moveToPriorAuthOpen, setMoveToPriorAuthOpen] = useState(false);
  const [moveToPriorAuthBusy, setMoveToPriorAuthBusy] = useState(false);
  const [priorAuthToPendingBusy, setPriorAuthToPendingBusy] = useState(false);
  // Checkbox save failure toast — auto-dismisses; never a silent reset.
  const [taskError, setTaskError] = useState(null);
  useEffect(() => {
    if (!taskError) return undefined;
    const t = setTimeout(() => setTaskError(null), 5000);
    return () => clearTimeout(t);
  }, [taskError]);
  const [payorData, setPayorData] = useState({
    insuranceCode: referral.insurance_code || "",
    employerCode: referral.employer_code || "",
  });
  // Raintree-sync watermark for the badge. Re-fetched every time the open
  // referral changes AND after a save (via the bump counter) so the dot
  // flips from amber → green once n8n marks the queue rows processed.
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncBump, setSyncBump] = useState(0);

  useEffect(() => {
    if (!referral?.file_id) return;
    let cancelled = false;
    // Ticket C (2026-08-24; regression fix): await the shared /card
    // promise instead of racing it. If a prefetch is registered
    // (mount-time — see startCardPrefetch in the parent), this awaits
    // the SAME request the detail effect is already awaiting — one
    // network round-trip, not two. When no prefetch exists (edit-flow
    // syncBump refetches always miss because startCardPrefetch is
    // only called on file_id CHANGE), fall through to the individual
    // endpoint.
    (async () => {
      const prefetched = await awaitCardPrefetch(referral.file_id, "sync_status");
      if (cancelled) return;
      if (prefetched) {
        setSyncStatus(prefetched);
        return;
      }
      try {
        const res = await axios.get(
          `${API}/api/intake/referrals/${referral.file_id}/sync-status`,
          { headers: { Authorization: `Bearer ${getStoredToken()}` } }
        );
        if (!cancelled) setSyncStatus(res.data);
      } catch {
        if (!cancelled) setSyncStatus(null);
      }
    })();
    return () => { cancelled = true; };
  }, [referral?.file_id, syncBump]);

  // ── Incomplete-tab actions ─────────────────────────────────────────────
  // Reads `missing_fields` straight off the row so the user sees exactly
  // what's outstanding before they override. Empty string / null = none.
  const missingList = (referral.missing_fields || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  // ── Reconciliation requirements (incomplete tab) ──────────────────────
  // Live payload from /requirements; refetched after any panel save or
  // therapy confirmation (reqBump). Drives the Required Information panel
  // and the two-mode move button — the SERVER re-verifies on every move.
  const [requirements, setRequirements] = useState(null);
  const [reqBump, setReqBump] = useState(0);
  // ── Accordion open/close state — React state ONLY (resets when the panel
  // closes / referral switches; deliberately no local/session storage).
  const [openSections, setOpenSections] = useState({ referral: true, patient: false, payor: false, additional: false });
  const accordionDefaultsRef = useRef(null);
  // Sticky footer (Status & Actions) collapse — DEFAULT COLLAPSED, React
  // state only (resets on referral switch; no local/session storage).
  const [footerOpen, setFooterOpen] = useState(false);
  useEffect(() => {
    accordionDefaultsRef.current = null;
    setOpenSections({ referral: true, patient: false, payor: false, additional: false });
    setFooterOpen(false);
  }, [referral.file_id]);
  // Confirm Therapy via the n8n bridge — selection ORDER matters (first
  // selected = primary/parent therapy per the bridge contract).
  const [confirmTherapies, setConfirmTherapies] = useState([]);
  const [confirmTherapyBusy, setConfirmTherapyBusy] = useState(false);
  // Last successful bridge response — drives the children result block and
  // the needs_manual_clinic callout.
  const [confirmResult, setConfirmResult] = useState(null);
  // 503 from the confirm endpoint = bridge not configured; disable the flow
  // with the server's message verbatim.
  const [bridgeDownMsg, setBridgeDownMsg] = useState(null);
  const [partialConfirmOpen, setPartialConfirmOpen] = useState(false);
  useEffect(() => {
    if (!isIncompleteContext || !referral?.file_id) {
      setRequirements(null);
      return undefined;
    }
    let cancelled = false;
    // New referral opened — clear stale confirm-flow state.
    setConfirmTherapies([]);
    setConfirmResult(null);
    // Ticket C (2026-08-24; regression fix): await the shared /card
    // promise so we don't fire a second individual request for the
    // same data the batch is already fetching. Edit-flow refetches
    // (reqBump-driven remounts on save) miss the prefetch because
    // startCardPrefetch is only called on file_id change; they hit
    // the individual endpoint below — correct.
    (async () => {
      const prefetched = await awaitCardPrefetch(referral.file_id, "requirements");
      if (cancelled) return;
      if (prefetched) {
        setRequirements(prefetched);
        return;
      }
      try {
        const res = await axios.get(
          `${API}/api/intake/referrals/${referral.file_id}/requirements`,
          { headers: { Authorization: `Bearer ${getStoredToken()}` } }
        );
        if (!cancelled) setRequirements(res.data);
      } catch {
        if (!cancelled) setRequirements(null);
      }
    })();
    return () => { cancelled = true; };
  }, [isIncompleteContext, referral?.file_id, reqBump]);

  const saveReqField = async (name, value) => {
    await handleSaveField(name, value);
    setReqBump((b) => b + 1);
  };

  // ── Shared missing derivation — straight off the /requirements payload.
  // Feeds the summary banner, per-section chips, Missing field tags and the
  // default-expansion rules. NEVER a second rules engine.
  const reqMissingByKey = useMemo(() => {
    const m = {};
    if (requirements) {
      if (!requirements.case_type_set) m.case_type = true;
      requirements.fields.forEach((f) => { if (f.missing) m[f.key] = true; });
      if (requirements.case_type_set && requirements.therapy?.pending) m.therapy_confirmation = true;
    }
    return m;
  }, [requirements]);
  const sectionMissing = useMemo(() => {
    const counts = { referral: 0, patient: 0, payor: 0, additional: 0 };
    Object.keys(reqMissingByKey).forEach((k) => {
      counts[REQ_KEY_SECTION[k] || "additional"] += 1;
    });
    return counts;
  }, [reqMissingByKey]);
  // Requirements-only editors (home phone, address, physician, attorney…)
  // routed into their sections; static referral.* editors are skipped.
  const reqExtraFields = useMemo(() => {
    const out = { referral: [], patient: [], payor: [], additional: [] };
    if (requirements?.case_type_set) {
      requirements.fields.forEach((f) => {
        if (STATIC_FIELD_KEYS.has(f.key)) return;
        out[REQ_KEY_SECTION[f.key] || "additional"].push(f);
      });
    }
    return out;
  }, [requirements]);
  // Default expansion (once per referral, when requirements first land):
  // sections with missing required fields open; complete sections closed.
  useEffect(() => {
    if (!requirements || accordionDefaultsRef.current === referral.file_id) return;
    accordionDefaultsRef.current = referral.file_id;
    setOpenSections({
      referral: sectionMissing.referral > 0,
      patient: sectionMissing.patient > 0,
      payor: sectionMissing.payor > 0,
      additional: sectionMissing.additional > 0,
    });
  }, [requirements, sectionMissing, referral.file_id]);
  const toggleSection = (id) => setOpenSections((s) => ({ ...s, [id]: !s[id] }));
  // Banner click: expand every section that still has missing items (all
  // sections when nothing is missing).
  const expandMissingSections = () =>
    setOpenSections((s) => {
      if (!requirements || requirements.missing_count === 0) {
        return { referral: true, patient: true, payor: true, additional: true };
      }
      return {
        referral: s.referral || sectionMissing.referral > 0,
        patient: s.patient || sectionMissing.patient > 0,
        payor: s.payor || sectionMissing.payor > 0,
        additional: s.additional || sectionMissing.additional > 0,
      };
    });
  const showReqChips = isIncompleteContext && !!requirements;
  // Requirements-only editor renderer (identical EditableField wiring to
  // the old amber panel — saveReqField + reqBump refetch preserved).
  const renderReqExtraField = (f) => {
    // 2026-06 address split: compact group — Street (street only; legacy
    // crammed values display as-is until staff correct them) + City/State/
    // Zip row. Four independent saves through the existing generic PUT;
    // each empty part carries its own Missing tag off the SAME composite
    // requirement entry (no new derivation).
    if (f.key === "patient_address") {
      const p = f.parts || {};
      const emptyPart = (c) => !String(p[c] ?? "").trim();
      const addrPart = (col, label, placeholder = "") => (
        <ReqFieldSlot missing={!!f.missing && emptyPart(col)} fieldKey={col}>
          <EditableField label={label} value={p[col]} fieldName={col} onSave={saveReqField} canEdit={canEditFields} placeholder={placeholder} missing={!!f.missing && emptyPart(col)} />
        </ReqFieldSlot>
      );
      return (
        <div key={f.key} className="sm:col-span-2 space-y-2" data-testid="address-group">
          {addrPart("patient_address", "Street", "Street address only")}
          <div className="grid grid-cols-3 gap-2">
            {addrPart("patient_city", "City")}
            {addrPart("patient_state", "State")}
            {addrPart("patient_zip_code", "Zip")}
          </div>
        </div>
      );
    }
    return (
      <ReqFieldSlot key={f.key} missing={!!f.missing} fieldKey={f.key} className={f.parts ? "sm:col-span-2" : ""}>
        {f.parts ? (
          <div className="grid grid-cols-2 gap-2">
            <EditableField label="First Name" value={f.parts.employee_first_name} fieldName="employee_first_name" onSave={saveReqField} canEdit={canEditFields} missing={!!f.missing} />
            <EditableField label="Last Name" value={f.parts.employee_last_name} fieldName="employee_last_name" onSave={saveReqField} canEdit={canEditFields} missing={!!f.missing} />
          </div>
        ) : (
          <EditableField label={f.label} value={f.value} fieldName={f.key} onSave={saveReqField} canEdit={canEditFields} type={f.key.startsWith("date_") ? "date" : "text"} missing={!!f.missing} />
        )}
      </ReqFieldSlot>
    );
  };
  // Exactly ONE State input for the patient address: when the address group
  // renders (requirements flow), its State editor IS the state field — the
  // standalone one below is hidden (both write patient_state via the same
  // generic PUT).
  const hasAddressGroup = reqExtraFields.patient.some((f) => f.key === "patient_address");

  const toggleConfirmTherapy = (t) => {
    setConfirmTherapies((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]
    );
  };

  const handleConfirmTherapy = async () => {
    if (readOnly) return;
    setConfirmTherapyBusy(true);
    try {
      const res = await axios.post(
        `${API}/api/intake/referrals/${referral.file_id}/confirm-therapy`,
        { therapies: confirmTherapies },
        { headers: { Authorization: `Bearer ${getStoredToken()}` } }
      );
      setConfirmResult(res.data);
      setConfirmTherapies([]);
      if (res.data?.parent_completed && res.data?.step3_triggered) {
        alert("Case completed — Raintree sync triggered");
      } else {
        alert("Therapy confirmed");
      }
      setReqBump((b) => b + 1);
      onUpdate?.();
    } catch (err) {
      console.error("Confirm therapy failed:", err);
      const status = err?.response?.status;
      const msg = formatApiError(err, "Failed to confirm therapy");
      if (status === 503) setBridgeDownMsg(msg);
      // Bridge errors surface verbatim; the 502 is explicitly retry-safe.
      alert(status === 502 ? `${msg} Retrying is safe.` : msg);
    } finally {
      setConfirmTherapyBusy(false);
    }
  };

  // Complete branch offered ONLY when case type is set and nothing is
  // missing — never offered on unknown case_type (hard gate).
  const canComplete = !!(requirements && requirements.case_type_set && requirements.missing_count === 0);
  const missingLabels = requirements
    ? [
        ...(!requirements.case_type_set ? ["Case Type"] : []),
        ...requirements.fields.filter((f) => f.missing).map((f) => f.label),
        ...(requirements.case_type_set && requirements.therapy?.pending ? ["Therapy confirmation (Physical Therapy / Acupuncture / Aquatic Therapy)"] : []),
      ]
    : missingList;

  const handleMoveToPending = async () => {
    if (readOnly) return;
    // Two-mode: the server verifies on every call; this client-side check
    // only picks the UX path. Complete → straight through; otherwise a
    // soft-confirm modal replaces the old window.confirm.
    if (canComplete) {
      await doMoveToPending();
      return;
    }
    setPartialConfirmOpen(true);
  };

  const doMoveToPending = async () => {
    setMoveBusy(true);
    try {
      const res = await axios.post(
        `${API}/api/intake/incomplete/${referral.file_id}/move-to-pending`,
        {},
        { headers: { Authorization: `Bearer ${getStoredToken()}` } }
      );
      if (res.data?.branch === "complete") {
        alert(
          res.data.step3_triggered
            ? "Moved to pending — Raintree sync triggered"
            : "Moved to pending — warning: the Raintree sync trigger failed and will need a retry"
        );
      } else {
        alert("Referral moved to Pending.");
      }
      setPartialConfirmOpen(false);
      onClose?.();
      onUpdate?.();
    } catch (err) {
      console.error("Move to pending failed:", err);
      alert(formatApiError(err, "Failed to move referral to Pending"));
    } finally {
      setMoveBusy(false);
    }
  };

  const handleMoveToCancelled = async ({ reason, notes }) => {
    setMoveBusy(true);
    try {
      await axios.post(
        `${API}/api/intake/incomplete/${referral.file_id}/move-to-cancelled`,
        { reason, notes },
        { headers: { Authorization: `Bearer ${getStoredToken()}` } }
      );
      alert("Referral moved to Cancelled.");
      setMoveCancelOpen(false);
      onClose?.();
      onUpdate?.();
    } catch (err) {
      console.error("Move to cancelled failed:", err);
      alert(formatApiError(err, "Failed to move referral to Cancelled"));
    } finally {
      setMoveBusy(false);
    }
  };

  // Pending → Incomplete: amber-coloured override for accidentally-promoted
  // referrals. Confirmation is rendered as an in-page React modal (state
  // `moveToIncompleteOpen`) rather than `window.confirm`, because the
  // preview iframe sandboxes/auto-dismisses native confirm dialogs which
  // made the earlier confirm-then-post path silently no-op. On success:
  // close modal + panel + refresh list so the row disappears from Pending
  // and reappears under Incomplete.
  const handleMoveToIncomplete = async () => {
    if (!moveToIncompleteReason) return; // reason is required going forward
    setMoveToIncompleteBusy(true);
    try {
      await axios.post(
        `${API}/api/intake/pending/${referral.file_id}/move-to-incomplete`,
        { reason: moveToIncompleteReason },
        { headers: { Authorization: `Bearer ${getStoredToken()}` } }
      );
      alert("Referral moved to Incomplete.");
      setMoveToIncompleteOpen(false);
      onClose?.();
      onUpdate?.();
    } catch (err) {
      console.error("Move to incomplete failed:", err);
      alert(formatApiError(err, "Failed to move referral to Incomplete"));
    } finally {
      setMoveToIncompleteBusy(false);
    }
  };

  // Pending → Needs Prior Auth: single-action flag (no reason dropdown),
  // confirmed via in-page modal for the same iframe-sandbox reason as
  // Move to Incomplete. On success the row hops tabs.
  const handleMoveToPriorAuth = async () => {
    setMoveToPriorAuthBusy(true);
    try {
      await axios.post(
        `${API}/api/intake/pending/${referral.file_id}/move-to-prior-auth`,
        {},
        { headers: { Authorization: `Bearer ${getStoredToken()}` } }
      );
      alert("Referral moved to Needs Prior Auth.");
      setMoveToPriorAuthOpen(false);
      onClose?.();
      onUpdate?.();
    } catch (err) {
      console.error("Move to prior auth failed:", err);
      alert(formatApiError(err, "Failed to move referral to Needs Prior Auth"));
    } finally {
      setMoveToPriorAuthBusy(false);
    }
  };

  // Needs Prior Auth → Pending: mistake recovery — clears the flag. Single
  // action, no modal (non-destructive; mirrors Cancelled → Pending).
  const handleMovePriorAuthToPending = async () => {
    if (readOnly) return;
    setPriorAuthToPendingBusy(true);
    try {
      await axios.post(
        `${API}/api/intake/prior-auth/${referral.file_id}/move-to-pending`,
        {},
        { headers: { Authorization: `Bearer ${getStoredToken()}` } }
      );
      onClose?.();
      onUpdate?.();
    } catch (err) {
      console.error("Move prior auth to pending failed:", err);
      alert(formatApiError(err, "Failed to move referral back to Pending"));
    } finally {
      setPriorAuthToPendingBusy(false);
    }
  };

  // Cancelled → Pending: reverse of Cancel Referral. Single-action, no
  // modal — the action is non-destructive (the referral simply hops tabs)
  // and the previous cancellation reason is wiped from the PG audit row
  // by the backend so future re-cancellations start clean. The phi_audit_log
  // still records the un-cancellation for compliance.
  const handleMoveCancelledToPending = async () => {
    if (readOnly) return;
    setUnCancelBusy(true);
    try {
      await axios.post(
        `${API}/api/intake/cancelled/${referral.file_id}/move-to-pending`,
        {},
        { headers: { Authorization: `Bearer ${getStoredToken()}` } }
      );
      onClose?.();
      onUpdate?.();
    } catch (err) {
      console.error("Move cancelled to pending failed:", err);
      alert(formatApiError(err, "Failed to move referral back to Pending"));
    } finally {
      setUnCancelBusy(false);
    }
  };


  const handleUpdateTask = async (task, data = {}) => {
    if (readOnly) return;
    // 2026-06-02: client-side timeout so the spinner can NEVER hang
    // forever if the server response stalls. The backend now returns
    // as soon as the PG transfer row is durable (BigQuery mirror runs
    // async in the background), so 20s is far more than the request
    // should ever need. On timeout/error we un-spin and toast.
    // P0 hotfix: optimistic checkbox — mark the field pending BEFORE the
    // POST so the poll merges around it; on success keep it (the poll drops
    // it once BQ confirms), on final failure revert + visible toast.
    const statusField = TASK_STATUS_FIELD[task];
    if (statusField) onTaskPending({ [statusField]: true });
    setLoading(true);
    try {
      await axios.post(
        `${API}/api/intake/referrals/${referral.file_id}/update`,
        { task, completed: true, ...data },
        {
          headers: { Authorization: `Bearer ${getStoredToken()}` },
          timeout: 20000,
        }
      );
      onUpdate();
    } catch (err) {
      console.error("Failed to update task:", err);
      // Never a silent reset: revert the optimistic checkbox and toast.
      if (statusField) onTaskPending(null);
      const msg = err?.code === "ECONNABORTED"
        ? "Request timed out. The transfer may have completed on the server — please refresh in a moment."
        : "Save failed — please try again";
      setTaskError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelReferral = async ({ reason, notes }) => {
    setCancelling(true);
    try {
      await axios.patch(
        `${API}/api/intake/referrals/${referral.file_id}/cancel`,
        { reason, notes },
        { headers: { Authorization: `Bearer ${getStoredToken()}` } }
      );
      setCancelOpen(false);
      onUpdate();
      onClose();  // close detail panel — this referral is no longer in pending view
    } catch (err) {
      console.error("Failed to cancel referral:", err);
      alert(formatApiError(err, "Failed to cancel referral"));
    } finally {
      setCancelling(false);
    }
  };

  // Inline-edit handler. Sends a single field to the new generic
  // `PUT /api/intake/referrals/{file_id}` endpoint which writes back to
  // BigQuery `weststar_documents`. Special-cased `patient_name`: the BQ
  // schema stores it as separate first/last columns, so we split on the
  // first whitespace and send both at once.
  const handleSaveField = async (fieldName, newValue) => {
    const token = getStoredToken();
    let fields;
    if (fieldName === "patient_name") {
      const full = (newValue || "").trim().replace(/\s+/g, " ");
      if (!full) {
        alert("Patient name cannot be empty.");
        throw new Error("Empty patient name");
      }
      const idx = full.indexOf(" ");
      const first = idx === -1 ? full : full.slice(0, idx);
      const last = idx === -1 ? "" : full.slice(idx + 1);
      fields = {
        employee_first_name: first,
        employee_last_name: last || null,
      };
    } else {
      fields = { [fieldName]: newValue };
    }
    try {
      await axios.put(
        `${API}/api/intake/referrals/${referral.file_id}`,
        { fields },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Bubble up so parent refreshes list + the detail panel
      onUpdate?.();
      // Trigger an immediate sync-status refetch so the badge flips from
      // green → amber the instant a save lands (then back to green ~when
      // n8n marks the queue rows processed).
      setSyncBump((n) => n + 1);
    } catch (err) {
      console.error("Inline edit failed:", err);
      alert(formatApiError(err, "Failed to save change"));
      throw err;
    }
  };

  const isWC = referral.case_type === "WC";
  // ── Assigned Clinic paired dropdown — canonical options fetched once per
  // detail open; the pair endpoint is the ONLY writer for this field.
  const [clinicOptions, setClinicOptions] = useState([]);
  // Optimistic overlay for the clinic field: filled from the save
  // response, cleared when the panel switches referral (the list refresh
  // that follows re-reads BQ and agrees).
  const [clinicOverlay, setClinicOverlay] = useState(null);
  useEffect(() => { setClinicOverlay(null); }, [referral.file_id]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const token = getStoredToken();
        const res = await axios.get(`${API}/api/intake/referrals/clinic-options`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (alive) setClinicOptions(res.data.options || []);
      } catch (err) {
        console.error("Clinic options fetch failed:", err);
      }
    })();
    return () => { alive = false; };
  }, [referral.file_id]);

  const saveAssignedClinic = async (body) => {
    const token = getStoredToken();
    try {
      const res = await axios.post(
        `${API}/api/intake/referrals/${referral.file_id}/assigned-clinic`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Optimistic fill from the RESPONSE — the endpoint already returns
      // the canonical label, so the field updates immediately instead of
      // waiting on a BQ re-read (the save itself is seconds of BQ DML).
      const d = res.data || {};
      setClinicOverlay({
        name: d.assigned_clinic ?? null,
        city: d.assigned_clinic_city ?? null,
        note: d.note || null,
      });
      onUpdate?.();
      setSyncBump((n) => n + 1);
      return d;
    } catch (err) {
      console.error("Assigned clinic save failed:", err);
      alert(formatApiError(err, "Failed to update assigned clinic"));
      throw err;
    }
  };

  // Server truth merged with the optimistic overlay for in-flight/just-saved
  // checkbox writes — the overlay wins until the poll confirms (or reverts).
  const intakeStatus = { ...(referral.intake_status || {}), ...(taskOverlay || {}) };
  const isCancelled = referral.is_cancelled || intakeStatus.is_cancelled;
  // Edits are blocked once the referral has been finalised — cancelled rows
  // are archival, transferred rows belong to the CRM Queue edit flow.
  const canEditFields = !readOnly && !isCancelled && !intakeStatus.ready_for_crm;

  // ── Notes (single text blob, mirrors the CRM QueueBanner notes UX) ──────
  const [notesDraft, setNotesDraft] = useState(referral.notes || "");
  const [notesStamp, setNotesStamp] = useState({ at: referral.notes_updated_at, by: referral.notes_updated_by });
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);
  const notesHydratedRef = useRef(referral.notes !== undefined);

  useEffect(() => {
    notesHydratedRef.current = referral.notes !== undefined;
    setNotesDraft(referral.notes || "");
    setNotesStamp({ at: referral.notes_updated_at, by: referral.notes_updated_by });
    setNotesSaved(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referral.file_id]);

  useEffect(() => {
    // List payloads carry no notes — adopt them once the detail hydration
    // merge lands, without clobbering typing afterwards.
    if (!notesHydratedRef.current && referral.notes !== undefined) {
      notesHydratedRef.current = true;
      setNotesDraft(referral.notes || "");
      setNotesStamp({ at: referral.notes_updated_at, by: referral.notes_updated_by });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referral.notes]);

  const saveNotes = async () => {
    setNotesSaving(true);
    try {
      const res = await axios.put(
        `${API}/api/intake/referrals/${referral.file_id}/notes`,
        { notes: notesDraft },
        { headers: { Authorization: `Bearer ${getStoredToken()}` } }
      );
      setNotesStamp({ at: res.data.notes_updated_at, by: res.data.notes_updated_by });
      setNotesSaved(true);
    } catch (err) {
      alert(formatApiError(err, "Failed to save notes"));
    } finally {
      setNotesSaving(false);
    }
  };

  return (
    <div className="bg-white h-full flex flex-col overflow-hidden" data-testid="referral-detail-panel">
      <CancelReferralModal
        open={cancelOpen}
        busy={cancelling}
        patientName={referral.patient_name}
        onClose={() => !cancelling && setCancelOpen(false)}
        onConfirm={handleCancelReferral}
      />

      {/* Pending → Incomplete confirm modal. Matches the visual language of
          CancelReferralModal but only confirms — no reason/notes fields. */}
      {moveToIncompleteOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="move-to-incomplete-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden border border-slate-200">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
              <AlertTriangle size={18} className="text-amber-600" />
              <h3 className="font-bold text-slate-900">Move to Incomplete</h3>
            </div>
            <div className="p-5 text-sm text-slate-600 space-y-4">
              <p>
                Move the referral for{" "}
                <span className="font-medium text-slate-900">{referral.patient_name}</span>{" "}
                back to Incomplete? It will leave the Pending tab and reappear under Incomplete.
              </p>
              {/* Reason dropdown — mirrors the cancel-reason modal pattern.
                  Required going forward (confirm stays disabled until set). */}
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">
                  Reason <span className="text-rose-500">*</span>
                </label>
                <select
                  value={moveToIncompleteReason}
                  onChange={(e) => setMoveToIncompleteReason(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                  data-testid="move-to-incomplete-reason-select"
                >
                  <option value="">Select a reason…</option>
                  {INCOMPLETE_REASON_OPTIONS.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex justify-end gap-2">
              <button
                onClick={() => !moveToIncompleteBusy && setMoveToIncompleteOpen(false)}
                disabled={moveToIncompleteBusy}
                className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200 rounded-lg disabled:opacity-50"
                data-testid="move-to-incomplete-cancel"
              >
                Keep in Pending
              </button>
              <button
                onClick={handleMoveToIncomplete}
                disabled={moveToIncompleteBusy || !moveToIncompleteReason}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg disabled:opacity-50 flex items-center gap-2"
                data-testid="move-to-incomplete-confirm"
              >
                {moveToIncompleteBusy ? <Loader2 size={14} className="animate-spin" /> : <AlertTriangle size={14} />}
                Move to Incomplete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pending → Needs Prior Auth confirm modal — same pattern as the
          Move to Incomplete modal (confirm only, no reason/notes). */}
      {moveToPriorAuthOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="move-to-prior-auth-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden border border-slate-200">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
              <ClipboardCheck size={18} className="text-indigo-600" />
              <h3 className="font-bold text-slate-900">Move to Needs Prior Auth</h3>
            </div>
            <div className="p-5 text-sm text-slate-600">
              Move the referral for{" "}
              <span className="font-medium text-slate-900">{referral.patient_name}</span>{" "}
              to Needs Prior Auth? It will leave the Pending tab until the authorization is resolved.
            </div>
            <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex justify-end gap-2">
              <button
                onClick={() => !moveToPriorAuthBusy && setMoveToPriorAuthOpen(false)}
                disabled={moveToPriorAuthBusy}
                className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200 rounded-lg disabled:opacity-50"
                data-testid="move-to-prior-auth-cancel"
              >
                Keep in Pending
              </button>
              <button
                onClick={handleMoveToPriorAuth}
                disabled={moveToPriorAuthBusy}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50 flex items-center gap-2"
                data-testid="move-to-prior-auth-confirm"
              >
                {moveToPriorAuthBusy ? <Loader2 size={14} className="animate-spin" /> : <ClipboardCheck size={14} />}
                Move to Needs Prior Auth
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Partial reconciliation soft-confirm — the server still verifies. */}
      {partialConfirmOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="partial-move-confirm-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden border border-slate-200">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
              <AlertTriangle size={18} className="text-amber-600" />
              <h3 className="font-bold text-slate-900">Move with missing information?</h3>
            </div>
            <div className="p-5 text-sm text-slate-600 space-y-3">
              <p className="font-medium text-slate-800">
                This referral will NOT be sent to Raintree until required fields are completed.
              </p>
              {missingLabels.length > 0 && (
                <ul className="list-disc pl-5 space-y-0.5 text-xs" data-testid="partial-missing-list">
                  {missingLabels.map((l) => (
                    <li key={l}>{l}</li>
                  ))}
                </ul>
              )}
            </div>
            <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex justify-end gap-2">
              <button
                onClick={() => !moveBusy && setPartialConfirmOpen(false)}
                disabled={moveBusy}
                className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200 rounded-lg disabled:opacity-50"
                data-testid="partial-move-cancel"
              >
                Keep in Incomplete
              </button>
              <button
                onClick={doMoveToPending}
                disabled={moveBusy}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg disabled:opacity-50 flex items-center gap-2"
                data-testid="partial-move-confirm"
              >
                {moveBusy ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                Move to Pending anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Checkbox save-failure toast (P0 hotfix) — visible, auto-dismisses. */}
      {taskError && (
        <div
          className="fixed bottom-6 right-6 z-[60] px-4 py-3 rounded-lg bg-red-600 text-white text-sm font-medium shadow-lg"
          role="alert"
          data-testid="task-save-error-toast"
        >
          {taskError}
        </div>
      )}

      {/* Read-only lock banner — another rep holds this referral's lock. */}
      {readOnly && (
        <div
          className="px-6 py-2.5 bg-amber-100 border-b border-amber-200 flex items-center gap-2 text-sm font-semibold text-amber-800"
          data-testid="intake-lock-readonly-banner"
        >
          <Lock size={14} className="flex-shrink-0" />
          Currently being worked by {lockedBy || "another rep"} — view only
        </div>
      )}

      {/* ── Zone 1: sticky header card (accordion restyle 2026-06) ─────────
          Case-type pill + sync chip + context actions on the top row; the
          patient name (still inline-editable) + DOB below; compact
          dual-therapy line last. flex-shrink-0 keeps it pinned. */}
      <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex flex-col gap-1.5 flex-shrink-0" data-testid="detail-header-zone">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className="px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide bg-slate-800 text-white"
            data-testid="case-type-pill"
          >
            {referral.case_type || "—"}
          </span>
          <SyncStatusBadge status={syncStatus} />
          <span className="flex-1" />
          {isPendingContext && !isCancelled && (
            <button
              onClick={() => setMoveToIncompleteOpen(true)}
              disabled={moveToIncompleteBusy || readOnly}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-800 bg-amber-50 hover:bg-amber-100 border border-amber-300 rounded-lg transition-colors disabled:opacity-50"
              title="Move this referral back to Incomplete"
              data-testid="move-to-incomplete-btn"
            >
              {moveToIncompleteBusy ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <AlertTriangle size={14} />
              )}
              Move to Incomplete
            </button>
          )}
          {isPendingContext && !isCancelled && (
            <button
              onClick={() => setMoveToPriorAuthOpen(true)}
              disabled={moveToPriorAuthBusy || readOnly}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-800 bg-indigo-50 hover:bg-indigo-100 border border-indigo-300 rounded-lg transition-colors disabled:opacity-50"
              title="Move this referral to Needs Prior Auth"
              data-testid="move-to-prior-auth-btn"
            >
              {moveToPriorAuthBusy ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <ClipboardCheck size={14} />
              )}
              Move to Prior Auth
            </button>
          )}
          {isPriorAuthContext && !isCancelled && (
            <button
              onClick={handleMovePriorAuthToPending}
              disabled={priorAuthToPendingBusy || readOnly}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 rounded-lg transition-colors disabled:opacity-50"
              title="Move this referral back to Pending"
              data-testid="prior-auth-move-to-pending-btn"
            >
              {priorAuthToPendingBusy ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <CheckCircle2 size={14} />
              )}
              Move to Pending
            </button>
          )}
          {isCancelledContext && isCancelled && (
            <button
              onClick={handleMoveCancelledToPending}
              disabled={unCancelBusy || readOnly}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 rounded-lg transition-colors disabled:opacity-50"
              title="Move this referral back to Pending"
              data-testid="move-cancelled-to-pending-btn"
            >
              {unCancelBusy ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <CheckCircle2 size={14} />
              )}
              Move to Pending
            </button>
          )}
          {!isCancelled && (
            <button
              onClick={() => setCancelOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded-lg transition-colors"
              title="Cancel this referral"
              data-testid="open-cancel-referral-btn"
            >
              <Ban size={14} />
              Cancel Referral
            </button>
          )}
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
            data-testid="close-detail-panel"
          >
            <X size={18} />
          </button>
        </div>
        <div className="min-w-0 flex items-end justify-between gap-3">
          <div className="min-w-0 flex-1">
            <EditableField
              label="Patient Name"
              value={referral.patient_name}
              fieldName="patient_name"
              onSave={handleSaveField}
              canEdit={canEditFields}
              placeholder="First Last"
            />
          </div>
          <p
            className="text-xs text-slate-500 font-mono whitespace-nowrap pb-0.5"
            data-testid="intake-detail-dob"
          >
            DOB: {formatDOB(referral.date_of_birth)}
          </p>
        </div>
        {(requirements?.dual_therapy || referral.dual_therapy) && (
          <p className="text-[11px] font-medium text-violet-700" data-testid="dual-therapy-detail-line">
            Dual-therapy referral — this patient has a second case for the other therapy type.
          </p>
        )}
      </div>

      {/* Cancelled banner */}
      {isCancelled && (
        <div className="px-6 py-3 bg-rose-50 border-b border-rose-200 flex items-start gap-2" data-testid="cancelled-banner">
          <XCircle size={18} className="text-rose-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-rose-800">
              Referral cancelled
              {referral.cancellation?.reason
                ? ` — ${CANCEL_REASON_LABEL[referral.cancellation.reason] || referral.cancellation.reason}`
                : ""}
            </p>
            {referral.cancellation?.notes && (
              <p className="text-rose-700 mt-0.5">{referral.cancellation.notes}</p>
            )}
            {referral.cancellation?.cancelled_by && (
              <p className="text-xs text-rose-600 mt-1">
                by {referral.cancellation.cancelled_by}
                {referral.cancellation?.cancelled_at
                  ? ` on ${new Date(referral.cancellation.cancelled_at).toLocaleString()}`
                  : ""}
              </p>
            )}
          </div>
        </div>
      )}

      {/* ── Required Information summary banner — above the accordions,
          requirements payload only. Amber while items are missing, green at
          zero; clicking expands every section that still has missing items.
          Same derivation as the chips (requirements.missing_count). ── */}
      {isIncompleteContext && requirements && (
        <button
          type="button"
          onClick={expandMissingSections}
          className={`flex-shrink-0 mx-3 mt-2 px-3 py-1.5 rounded-lg border text-xs font-semibold flex items-center justify-between gap-2 text-left transition-colors ${
            requirements.missing_count > 0
              ? "bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100"
              : "bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100"
          }`}
          title="Expand sections with missing items"
          data-testid="req-summary-banner"
        >
          <span data-testid="required-missing-count">
            {requirements.missing_count > 0
              ? `Required Information — ${requirements.missing_count} of ${requirements.total_count} required items missing`
              : `Required Information — all ${requirements.total_count} required items complete`}
          </span>
          <ChevronDown size={14} className="flex-shrink-0" />
        </button>
      )}

      {/* ── Zone 2: scrollable accordion middle (the pinned status zone
          below sits OUTSIDE this scroller, always visible) ── */}
      <div className="flex-1 min-h-0 overflow-y-auto [scrollbar-gutter:stable] px-3 py-2 space-y-2" data-testid="detail-scroll-region">
        {/* All visible patient fields stay inline-editable (Pending +
            Incomplete tabs only) — saves write back to BigQuery via
            PUT /api/intake/referrals/{file_id}, exactly as before. */}
        <AccordionSection id="referral" title="Referral Information" open={openSections.referral} onToggle={toggleSection} missingCount={sectionMissing.referral} showChip={showReqChips}>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-2.5 gap-y-2">
            {/* Case Type FIRST — HARD GATE: until set, the Complete branch
                is unavailable entirely (no minimal-list completion). */}
            <div className={requirements && !requirements.case_type_set ? "ring-1 ring-rose-400 rounded-md p-2 bg-rose-50/50" : ""} data-testid="req-case-type">
              <EditableField label="Case Type" value={referral.case_type} fieldName="case_type" onSave={saveReqField} canEdit={canEditFields} options={["WC", "PI"]} />
              {requirements && !requirements.case_type_set && (
                <p className="text-[11px] font-medium text-rose-600 mt-1" data-testid="case-type-gate-flag">
                  Set Case Type to determine requirements
                </p>
              )}
            </div>
            <EditableField label="Intake Type" value={referral.intake_type} fieldName="intake_type" onSave={handleSaveField} canEdit={canEditFields} options={INTAKE_TYPE_OPTIONS} />
            <EditableField label="Referral ID" value={referral.raintree_referral_id} fieldName="raintree_referral_id" onSave={handleSaveField} canEdit={canEditFields} />
            <EditableField
              label={
                <span className="flex items-center gap-1.5">
                  Referral NPI
                  {referral.needs_npi_review && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-700">
                      <AlertCircle size={10} /> Needs review
                    </span>
                  )}
                </span>
              }
              value={referral.linked_referral_npi}
              fieldName="linked_referral_npi"
              onSave={handleSaveField}
              canEdit={canEditFields}
              placeholder="10 digits"
            />
            <div>
              <p className="text-xs text-slate-500">Case #</p>
              <p className="font-medium text-slate-900">{referral.raintree_case_id || "—"}</p>
            </div>
            <ReqFieldSlot missing={!!reqMissingByKey.claim_number} fieldKey="claim_number">
              <EditableField label="Claim #" value={referral.claim_number} fieldName="claim_number" onSave={saveReqField} canEdit={canEditFields} missing={!!reqMissingByKey.claim_number} />
            </ReqFieldSlot>
            <ReqFieldSlot missing={!!reqMissingByKey.date_of_injury} fieldKey="date_of_injury">
              <EditableField label="Date of Injury" value={referral.date_of_injury} fieldName="date_of_injury" onSave={saveReqField} canEdit={canEditFields} type="date" missing={!!reqMissingByKey.date_of_injury} />
            </ReqFieldSlot>
            <ReqFieldSlot missing={!!reqMissingByKey.date_of_accident} fieldKey="date_of_accident">
              <EditableField label="Date of Accident" value={referral.date_of_accident} fieldName="date_of_accident" onSave={saveReqField} canEdit={canEditFields} type="date" missing={!!reqMissingByKey.date_of_accident} />
            </ReqFieldSlot>
            {reqExtraFields.referral.map(renderReqExtraField)}
            <ReqFieldSlot missing={!!reqMissingByKey.body_parts_involved} fieldKey="body_parts_involved" className="sm:col-span-2 xl:col-span-3">
              <EditableField label="Body Parts Involved" value={referral.body_parts} fieldName="body_parts_involved" onSave={saveReqField} canEdit={canEditFields} missing={!!reqMissingByKey.body_parts_involved} />
            </ReqFieldSlot>
            <div className="sm:col-span-2 xl:col-span-3">
              <EditableField label="Referral Reason" value={referral.referral_reason} fieldName="referral_reason" onSave={handleSaveField} canEdit={canEditFields} />
            </div>
          </div>

            {/* Therapy — pending ONLY from the stored missing_fields label;
                eligible rows are satisfied only via the Confirm Therapy
                bridge flow (the single writer — direct edits would bypass
                child-case creation). Pinned INSIDE Referral Information. */}
            {requirements && (() => {
              const therapyEligible = !!requirements.therapy?.confirm_eligible;
              return (
                <div className="mt-3 pt-2 border-t border-slate-100 space-y-2" data-testid="req-therapy-row">
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      {/* Read-only while confirm-eligible: a staff member
                          "fixing" 'PT, AP' to one value via the dropdown must
                          be impossible — the Confirm flow is the only correct
                          splitter. */}
                      <EditableField label="Therapy Type" value={requirements.therapy?.therapy_type} fieldName="therapy_type" onSave={saveReqField} canEdit={canEditFields && !therapyEligible} options={THERAPY_TYPE_OPTIONS} />
                      {therapyEligible && (
                        <p className="text-[11px] font-medium text-amber-700 mt-0.5" data-testid="therapy-locked-hint">
                          Use Confirm Therapy below
                        </p>
                      )}
                    </div>
                    {requirements.therapy?.pending && (
                      <span className="text-[10px] font-bold uppercase text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded mb-1" data-testid="therapy-pending-tag">Pending</span>
                    )}
                    {!therapyEligible && (
                      <span className="text-[10px] font-bold uppercase text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded mb-1" data-testid="therapy-confirmed-tag">Confirmed</span>
                    )}
                  </div>

                  {therapyEligible && (
                    <div className="border border-amber-200 bg-white rounded-md p-2.5 space-y-2" data-testid="confirm-therapy-block">
                      <p className="text-xs font-semibold text-amber-800">
                        Therapy confirmation (Physical Therapy / Acupuncture / Aquatic Therapy)
                      </p>
                      <div className="flex flex-wrap gap-3">
                        {THERAPY_TYPE_OPTIONS.map((t) => (
                          <label key={t} className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={confirmTherapies.includes(t)}
                              onChange={() => toggleConfirmTherapy(t)}
                              disabled={confirmTherapyBusy || readOnly || !!bridgeDownMsg}
                              data-testid={`confirm-therapy-option-${t.replace(/\s+/g, "-").toLowerCase()}`}
                            />
                            {t}
                          </label>
                        ))}
                      </div>
                      <p className="text-[10px] text-slate-500">
                        Select 1–3 — the first selected is the primary (parent) therapy.
                      </p>
                      {bridgeDownMsg && (
                        <p className="text-[11px] font-medium text-rose-600" data-testid="bridge-unconfigured-msg">{bridgeDownMsg}</p>
                      )}
                      <button
                        onClick={handleConfirmTherapy}
                        disabled={confirmTherapyBusy || readOnly || !!bridgeDownMsg || confirmTherapies.length < 1 || confirmTherapies.length > 3}
                        className="px-2.5 py-1 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded disabled:opacity-50"
                        data-testid="confirm-therapy-btn"
                      >
                        {confirmTherapyBusy ? "Confirming — this can take up to 30 seconds…" : "Confirm Therapy"}
                      </button>
                    </div>
                  )}

                  {confirmResult?.children_created?.length > 0 && (
                    <div className="border border-violet-200 bg-violet-50 rounded-md p-2.5 space-y-1" data-testid="confirm-therapy-children-block">
                      <p className="text-xs font-semibold text-violet-800">Child case(s) created</p>
                      {confirmResult.children_created.map((c) => (
                        <p key={c.file_id} className="text-xs text-slate-700" data-testid={`confirm-child-${c.file_id}`}>
                          {c.file_id} — {c.therapy_type} — {c.assigned_clinic || "no clinic assigned"}
                        </p>
                      ))}
                      {confirmResult.children_created.filter((c) => c.needs_manual_clinic).map((c) => (
                        <p
                          key={`${c.file_id}-manual`}
                          className="text-xs font-bold text-rose-700 bg-rose-50 border border-rose-300 rounded px-2 py-1"
                          data-testid="needs-manual-clinic-callout"
                        >
                          Second case created but needs manual clinic assignment — no nearby clinic offers {c.therapy_type}. Notify scheduling.
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Document rules — informational only, never gate completion. */}
            {/* moved to the Additional accordion section below */}
        </AccordionSection>

        <AccordionSection id="patient" title="Patient Information" open={openSections.patient} onToggle={toggleSection} missingCount={sectionMissing.patient} showChip={showReqChips}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {reqExtraFields.patient.map(renderReqExtraField)}
            <ReqFieldSlot missing={!!reqMissingByKey.date_of_birth} fieldKey="date_of_birth">
              <EditableField label="Date of Birth" value={referral.date_of_birth} fieldName="date_of_birth" onSave={saveReqField} canEdit={canEditFields} type="date" missing={!!reqMissingByKey.date_of_birth} />
            </ReqFieldSlot>
            {!hasAddressGroup && (
              <EditableField label="State" value={referral.accident_state} fieldName="patient_state" onSave={handleSaveField} canEdit={canEditFields} />
            )}
            <SsnField fileId={referral.file_id} ssnState={referral.patient_ssn_state} last4={referral.patient_ssn_last4} onSave={handleSaveField} canEdit={canEditFields} />
            <EditableField label="Patient #" value={referral.raintree_patient_number} fieldName="raintree_patient_number" onSave={handleSaveField} canEdit={canEditFields} />
          </div>
        </AccordionSection>

        <AccordionSection id="payor" title="Payor & Billing" open={openSections.payor} onToggle={toggleSection} missingCount={sectionMissing.payor} showChip={showReqChips}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <ReqFieldSlot missing={!!reqMissingByKey.insurance_carrier} fieldKey="insurance_carrier">
              <EditableField label="Payor" value={referral.insurance_carrier} fieldName="insurance_carrier" onSave={saveReqField} canEdit={canEditFields} missing={!!reqMissingByKey.insurance_carrier} />
            </ReqFieldSlot>
            <ReqFieldSlot missing={!!reqMissingByKey.employer_name} fieldKey="employer_name">
              <EditableField label="Payor" value={referral.employer_name} fieldName="employer_name" onSave={saveReqField} canEdit={canEditFields} missing={!!reqMissingByKey.employer_name} />
            </ReqFieldSlot>
            <div className="sm:col-span-2">
              <ClinicSelectField
                name={clinicOverlay ? clinicOverlay.name : referral.assigned_clinic}
                city={clinicOverlay ? clinicOverlay.city : referral.assigned_clinic_city}
                note={clinicOverlay?.note || null}
                options={clinicOptions}
                therapyType={referral.therapy_type || requirements?.therapy?.therapy_type || null}
                onSave={saveAssignedClinic}
                canEdit={canEditFields}
              />
            </div>
            {reqExtraFields.payor.map(renderReqExtraField)}
          </div>
        </AccordionSection>

        <AccordionSection id="additional" title="Additional" open={openSections.additional} onToggle={toggleSection} missingCount={sectionMissing.additional} showChip={false}>
          {requirements && (
            <div className="space-y-2 mb-2">
              {/* Document rules — informational only, never gate completion. */}
              {requirements.documents?.length > 0 && (
                <div className="space-y-1" data-testid="req-documents">
                  {requirements.documents.map((d) => (
                    <div key={d.key} className="flex items-center gap-2 text-xs text-slate-600">
                      {d.satisfied ? <Check size={13} className="text-emerald-600" /> : <X size={13} className="text-rose-500" />}
                      {d.label}
                      <span className="text-[10px] text-slate-400">(informational)</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Option A: not gated, unflagged, uncounted — editable only. */}
              <div data-testid="req-additional">
                <p className="text-[10px] font-semibold uppercase text-slate-400 mb-1">Additional (not required)</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {requirements.additional?.map((a) => (
                    <EditableField key={a.key} label={a.label} value={a.value} fieldName={a.key} onSave={saveReqField} canEdit={canEditFields} />
                  ))}
                </div>
              </div>

              {requirements.raintree_sync_status && (
                <p className="text-[11px] text-slate-400" data-testid="req-raintree-sync">
                  Raintree sync: {requirements.raintree_sync_status}
                </p>
              )}
            </div>
          )}

        {/* ICD-10 Codes (Feature 2 Phase 1, display only — edit comes in
            Phase 2). Renders the parsed list from the backend defensive
            parser; an empty list (or unparseable BQ row) shows the
            "No codes / needs review" copy. The red badge follows the
            single-source-of-truth `icd10_review` flag from the backend. */}
        <Icd10Panel
          codes={referral.icd10_codes}
          review={referral.icd10_review}
          fileId={referral.file_id}
          userRole={user?.role}
          onUpdate={onUpdate}
        />

        {/* Notes — directly under the ICD-10 block, both pending and
            incomplete tabs. Mirrors the CRM QueueBanner notes UX. In the
            read-only (locked) view the textarea is disabled and Save is
            hidden — last-write-wins on a shared blob makes viewer-saves a
            clobber risk; the lock holder owns all mutations. */}
        <div className="pt-2" data-testid="referral-notes-section">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide flex items-center gap-1.5">
              <StickyNote size={12} className="text-slate-400" />
              Referral Notes
            </label>
            {!readOnly && (
              <button
                data-testid="save-referral-notes-btn"
                onClick={saveNotes}
                disabled={notesSaving}
                className={`h-7 px-3 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
                  notesSaved
                    ? "bg-emerald-100 text-emerald-700 border border-emerald-300"
                    : "bg-slate-800 hover:bg-slate-700 text-white"
                } disabled:opacity-50`}
              >
                {notesSaving ? (
                  <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : notesSaved ? (
                  "Saved!"
                ) : (
                  "Save Notes"
                )}
              </button>
            )}
          </div>
          <textarea
            data-testid="referral-notes-input"
            value={notesDraft}
            onChange={(e) => { setNotesDraft(e.target.value); setNotesSaved(false); }}
            placeholder="Add intake notes, missing-info reminders, callback context..."
            rows={3}
            disabled={readOnly}
            className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none bg-slate-50 disabled:opacity-60 disabled:cursor-not-allowed"
          />
          {notesStamp.at && (
            <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
              <Clock size={10} />
              Updated {formatRelativeTime(notesStamp.at)}
              {notesStamp.by && <span>by <span className="font-medium text-slate-500">{notesStamp.by}</span></span>}
            </p>
          )}
        </div>
        </AccordionSection>

        {/* ── Status & Actions — INLINE collapsible block (2026-06): flows
            in the scroll region directly below the Additional section (no
            longer sticky/pinned). DEFAULT COLLAPSED: a slim summary strip
            (title + Billing/Payor/Ready state chips + missing count +
            chevron); expanding reveals EXACTLY the previous content —
            every checkbox/chip/task/button wired identically. */}
        <div className="border border-slate-200 rounded-lg bg-white" data-testid="status-actions-block">
        <button
          type="button"
          onClick={() => setFooterOpen((o) => !o)}
          className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-slate-50 rounded-lg transition-colors"
          data-testid="footer-toggle"
          aria-expanded={footerOpen}
        >
          <span className="text-sm font-bold text-slate-900">Status &amp; Actions</span>
          <span className="flex items-center gap-1.5 flex-shrink-0">
            <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${intakeStatus.billing_complete ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid="footer-chip-billing">
              Billing {intakeStatus.billing_complete ? "✓" : "—"}
            </span>
            <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${intakeStatus.payor_complete ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid="footer-chip-payor">
              Payor {intakeStatus.payor_complete ? "✓" : "—"}
            </span>
            <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${intakeStatus.ready_for_crm ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid="footer-chip-ready">
              Ready {intakeStatus.ready_for_crm ? "✓" : "—"}
            </span>
            {missingLabels.length > 0 && (
              <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-700" data-testid="footer-missing-count">
                {missingLabels.length} missing
              </span>
            )}
            {footerOpen ? (
              <ChevronDown size={14} className="text-slate-500" />
            ) : (
              <ChevronRight size={14} className="text-slate-500" />
            )}
          </span>
        </button>
        <div
          className={`grid transition-[grid-template-rows] duration-200 ${footerOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr] pointer-events-none"}`}
          data-testid="footer-body"
        >
          <div className="overflow-hidden min-h-0">
            <div className="p-2.5 pt-0 space-y-2" data-testid="footer-body-scroll">
        {/* Incomplete tab only — compact row: reason + live checklist.
            The billing checkbox here is the SAME wiring
            (handleUpdateTask("billing")); payor/ready render as status
            chips with their full controls inside the tasks expandable. */}
        {isIncompleteContext && (
          <div className="flex items-end gap-3 flex-wrap" data-testid="compact-checklist-row">
            <div className="flex-1 min-w-[180px]" data-testid="incomplete-reason-field">
              <EditableField label="Incomplete Reason" value={referral.incomplete_reason} fieldName="incomplete_reason" onSave={handleSaveField} canEdit={canEditFields} options={INCOMPLETE_REASON_OPTIONS} />
            </div>
            <label className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer pb-1">
              <input
                type="checkbox"
                checked={intakeStatus.billing_complete || false}
                onChange={() => {
                  if (!intakeStatus.billing_complete && !loading) {
                    handleUpdateTask("billing");
                  }
                }}
                disabled={loading || intakeStatus.billing_complete}
                className="w-4 h-4 rounded border-2 border-slate-300 text-emerald-500 focus:ring-emerald-500 cursor-pointer disabled:cursor-default"
                data-testid="billing-checkbox-compact"
              />
              Billing
            </label>
            <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded mb-1 ${intakeStatus.payor_complete ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid="payor-status-chip">
              Payor {intakeStatus.payor_complete ? "✓" : "—"}
            </span>
            <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded mb-1 ${intakeStatus.ready_for_crm ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid="ready-status-chip">
              Ready {intakeStatus.ready_for_crm ? "✓" : "—"}
            </span>
          </div>
        )}

        <TasksWrap collapse={isIncompleteContext}>
        {/* Task 1: Billing Information - Clickable Checkbox */}
        <div 
          className={`p-4 rounded-lg border transition-all ${
            intakeStatus.billing_complete
              ? "bg-emerald-50 border-emerald-200"
              : "bg-white border-slate-200"
          }`}
        >
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={intakeStatus.billing_complete || false}
              onChange={() => {
                if (!intakeStatus.billing_complete && !loading) {
                  handleUpdateTask("billing");
                }
              }}
              disabled={loading || intakeStatus.billing_complete}
              className="mt-1 w-5 h-5 rounded border-2 border-slate-300 text-emerald-500 focus:ring-emerald-500 cursor-pointer disabled:cursor-default"
              data-testid="billing-checkbox"
            />
            <div className="flex-1">
              <p className={`font-medium ${intakeStatus.billing_complete ? "text-emerald-700" : "text-slate-900"}`}>
                1. Update Billing Information in RainTree
              </p>
              <p className="text-sm text-slate-500 mt-0.5">
                Update the Billing Information Tab and Claim Information Section in RainTree
              </p>
              
              {!intakeStatus.billing_complete && (
                <>
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800 font-medium mb-2">Reminder: Update these fields in RainTree:</p>
                    <ul className="text-sm text-blue-700 space-y-1 ml-4 list-disc">
                      <li><strong>Auto Accident?</strong> — Enter <code className="bg-blue-100 px-1 rounded">A</code> if Auto, <code className="bg-blue-100 px-1 rounded">O</code> if Other</li>
                      <li><strong>Work Related?</strong> — Enter <code className="bg-blue-100 px-1 rounded">Y</code> or <code className="bg-blue-100 px-1 rounded">N</code></li>
                      <li><strong>State Where Accident Occurred</strong> — Enter state code (e.g., <code className="bg-blue-100 px-1 rounded">CA</code>)</li>
                    </ul>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleUpdateTask("billing")}
                    disabled={loading || readOnly}
                    className="mt-3 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                    data-testid="billing-complete-btn"
                  >
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                    I've Updated RainTree
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Task 2: Payor Information (WC Only) */}
        <TaskItem
          title="2. Update Payor Information in RainTree"
          description={isWC ? "Update Payor codes for Workers Comp case" : "Not required for non-WC cases"}
          complete={intakeStatus.payor_complete}
          disabled={!isWC}
          onClick={() => {}}
        >
          {isWC && (
            <div className="space-y-3">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>WC Case:</strong> Please update the Payor to{" "}
                  <span className="font-mono bg-amber-100 px-1 rounded">
                    {referral.insurance_carrier}
                  </span>{" "}
                  and Payor to{" "}
                  <span className="font-mono bg-amber-100 px-1 rounded">
                    {referral.employer_name}
                  </span>
                </p>
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Raintree Payor Code
                  </label>
                  <input
                    type="text"
                    value={payorData.insuranceCode}
                    onChange={(e) => setPayorData({ ...payorData, insuranceCode: e.target.value })}
                    placeholder="Enter code..."
                    className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Raintree Payor Code
                  </label>
                  <input
                    type="text"
                    value={payorData.employerCode}
                    onChange={(e) => setPayorData({ ...payorData, employerCode: e.target.value })}
                    placeholder="Enter code..."
                    className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm"
                  />
                </div>
              </div>
              {!intakeStatus.payor_complete && (
                <button
                  onClick={() => handleUpdateTask("payor", {
                    insurance_code: payorData.insuranceCode,
                    employer_code: payorData.employerCode,
                  })}
                  disabled={loading || !payorData.insuranceCode || readOnly}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? "Saving..." : "Mark Payor Complete"}
                </button>
              )}
            </div>
          )}
        </TaskItem>

        {/* Task 3: Ready for CRM */}
        <TaskItem
          title="3. Transfer to CRM Queue"
          description="Complete intake and move patient to the scheduling queue"
          complete={intakeStatus.ready_for_crm}
          onClick={() => {}}
        >
          {!intakeStatus.ready_for_crm && (
            <div className="space-y-3">
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <p className="text-sm text-emerald-800">
                  <strong>Note:</strong> This will remove the patient from Referral Intake and add them to the CRM Queue where sales can begin scheduling appointments.
                </p>
              </div>
              <button
                onClick={() => handleUpdateTask("ready", { completed: true })}
                disabled={loading || !intakeStatus.billing_complete || (isWC && !intakeStatus.payor_complete) || readOnly}
                className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
                data-testid="transfer-to-crm-btn"
              >
                {loading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <ArrowRight size={16} />
                )}
                Transfer to CRM Queue
              </button>
            </div>
          )}
          {intakeStatus.ready_for_crm && (
            <p className="text-sm text-emerald-600 font-medium flex items-center gap-2">
              <Check size={16} />
              Transferred to CRM Queue
            </p>
          )}
        </TaskItem>
        </TasksWrap>

        {/* Incomplete-tab manual overrides. Hidden everywhere else so the
            existing Pending/Cancelled UX stays exactly as it was. */}
        {isIncompleteContext && (
          <div className="pt-1.5 border-t border-slate-200">
            {missingList.length > 0 && (
              <p className="mb-2 text-[11px] text-amber-800 truncate" title={missingList.join(", ")} data-testid="missing-fields-line">
                <span className="font-semibold">Missing fields ({missingList.length}):</span> {missingList.join(", ")}
              </p>
            )}
            <div className="flex items-center gap-3" data-testid="incomplete-actions">
              <button
                onClick={handleMoveToPending}
                disabled={moveBusy || readOnly}
                className={`flex-1 px-4 py-2 text-white text-sm font-medium rounded-lg disabled:opacity-50 flex items-center justify-center gap-2 ${
                  canComplete ? "bg-green-600 hover:bg-green-700" : "bg-emerald-600 hover:bg-emerald-700"
                }`}
                data-testid="move-to-pending-btn"
                data-complete={canComplete ? "true" : "false"}
              >
                {moveBusy ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                {canComplete ? "Complete & Move to Pending" : "Move to Pending"}
              </button>
              <button
                onClick={() => setMoveCancelOpen(true)}
                disabled={moveBusy || readOnly}
                className="flex-1 px-4 py-2 bg-rose-600 text-white text-sm font-medium rounded-lg hover:bg-rose-700 disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="move-to-cancelled-btn"
              >
                {moveBusy ? <Loader2 size={16} className="animate-spin" /> : <Ban size={16} />}
                Move to Cancelled
              </button>
            </div>
            <CancelReferralModal
              open={moveCancelOpen}
              busy={moveBusy}
              patientName={referral.patient_name}
              reasons={INCOMPLETE_CANCEL_REASONS}
              title="Move to Cancelled"
              confirmLabel="Move to Cancelled"
              description={
                <>
                  Move{" "}
                  <span className="font-medium text-slate-900">{referral.patient_name}</span>{" "}
                  out of Incomplete and mark cancelled? Pick a reason for the audit log.
                </>
              }
              onClose={() => !moveBusy && setMoveCancelOpen(false)}
              onConfirm={handleMoveToCancelled}
            />
          </div>
        )}
            </div>
          </div>
        </div>
        </div>{/* end status-actions block */}

      </div>{/* end scroll region */}
    </div>
  );
}

export default function ReferralIntake() {
  const { user } = useAuth();
  const [referrals, setReferrals] = useState([]);
  // ── Checkbox write poll protection (P0 hotfix 2026-06) ──────────────────
  // While a checkbox write is in flight (incl. backend retries), the poll's
  // refetch must never repaint that checkbox from a stale BQ read. Pending
  // fields are tracked per file_id and poll data is merged AROUND them; an
  // entry drops off once the server confirms it (or the detail panel reverts
  // it on final failure).
  const [pendingTaskFields, setPendingTaskFields] = useState({});
  const pendingTaskFieldsRef = useRef({});
  pendingTaskFieldsRef.current = pendingTaskFields;
  const setTaskFieldsPending = useCallback((fileId, fields) => {
    setPendingTaskFields((prev) => {
      if (!fields) {
        if (!prev[fileId]) return prev;
        const next = { ...prev };
        delete next[fileId];
        return next;
      }
      return { ...prev, [fileId]: { ...(prev[fileId] || {}), ...fields } };
    });
  }, []);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [filter, setFilter] = useState(() => {
    // Patient Search deep-link prefill: /referral-intake?tab=…&q=…
    const t = new URLSearchParams(window.location.search).get("tab");
    return ["pending", "prior_auth", "incomplete", "cancelled"].includes(t) ? t : "pending";
  }); // 'pending' | 'prior_auth' | 'incomplete' | 'cancelled'
  const [search, setSearch] = useState(
    // NOTE: intake search is CLIENT-SIDE over loaded rows — older rows may
    // not be loaded, so the prefill can come up empty for old referrals.
    () => new URLSearchParams(window.location.search).get("q") || ""
  );
  // Date Added sort — two-state toggle (desc default, matching the server's
  // default order), mirroring the CRM Queue header pattern. Shared across
  // tabs (simplest consistent behavior); sent as sort_dir on every list fetch.
  const [dateAddedSort, setDateAddedSort] = useState("desc");
  // Cancelled tab recency window — server-side WHERE (never a client slice).
  // Search deep-link override (no-dead-link rule): arriving via Patient
  // Search's "Go to patient" (?tab=cancelled&q=…) fetches window=all so the
  // prefilled search always finds its row; normal navigation defaults 14d.
  const [cancelledWindow, setCancelledWindow] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get("tab") === "cancelled" && p.get("q") ? "all" : "14d";
  });
  // {shown, total} from the last cancelled fetch — drives the count chip.
  const [cancelledMeta, setCancelledMeta] = useState(null);

  // List payloads deliberately carry nothing SSN-related, so hydrate the
  // selected row with detail data (patient_ssn_last4 etc.) once on selection.
  const selectedFileId = selectedReferral?.file_id;

  // ── Split-screen workspace chrome ────────────────────────────────────────
  // Opening a referral covers the list; closing restores the exact list
  // scroll position. Esc is focus-guarded via the viewer's SHARED
  // isFormFieldTarget (never while typing in a field).
  const listScrollRef = useRef(0);
  useEffect(() => {
    if (!selectedFileId) window.scrollTo(0, listScrollRef.current);
  }, [selectedFileId]);
  useEffect(() => {
    if (!selectedFileId) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (isFormFieldTarget(e)) return;
      setSelectedReferral(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedFileId]);
  const selectedNeedsDetail = selectedReferral ? selectedReferral.patient_ssn_state === undefined : false;
  useEffect(() => {
    if (!selectedFileId || !selectedNeedsDetail) return;
    let stale = false;
    // Ticket C (2026-08-24; regression fix): register the batched
    // card request as a shared IN-FLIGHT PROMISE so the sync-status
    // and requirements effects await THE SAME request instead of
    // racing it and falling through to their own individual fetches
    // (the 18:59 log evidence: seven requests per open). The
    // downstream effects call `awaitCardPrefetch` — one-shot per
    // (file_id, sub-key). Documents were evicted from the batch —
    // RSA-signing is CPU-bound and holds the GIL.
    const cardPromise = startCardPrefetch(selectedFileId, () =>
      axios
        .get(`${API}/api/intake/referrals/${selectedFileId}/card`, {
          headers: { Authorization: `Bearer ${getStoredToken()}` },
        })
        .then((res) => res.data)
    );
    if (cardPromise) {
      cardPromise
        .then((card) => {
          if (stale || !card) return;
          // Merge, never replace, the checklist: the detail payload's
          // intake_status carries is_cancelled only (2026-10-22 fix).
          setSelectedReferral((cur) =>
            cur?.file_id === selectedFileId
              ? mergeDetail(cur, card.detail)
              : cur
          );
        })
        .catch(() => {
          // Batched endpoint blipped — fall back to the classic
          // detail fetch so a temporary /card outage never blanks
          // the panel.
          if (stale) return;
          clearCardPrefetch(selectedFileId);
          axios
            .get(`${API}/api/intake/referrals/${selectedFileId}`, {
              headers: { Authorization: `Bearer ${getStoredToken()}` },
            })
            .then((res2) => {
              if (stale) return;
              setSelectedReferral((cur) =>
                cur?.file_id === selectedFileId
                  ? mergeDetail(cur, res2.data)
                  : cur
              );
            })
            .catch(() => {}); // non-fatal
        });
    }
    return () => {
      stale = true;
    };
  }, [selectedFileId, selectedNeedsDetail]);

  // ── Referral lock (same semantics as the CRM Queue lock) ──────────────
  const intakeSessionId = useMemo(() => getIntakeSessionId(), []);
  const [lockConflict, setLockConflict] = useState(null); // {locked_by_username, locked_at} | null
  const lockConflictRef = useRef(null);
  lockConflictRef.current = lockConflict;

  useEffect(() => {
    const fid = selectedReferral?.file_id;
    if (!fid) {
      setLockConflict(null);
      return;
    }
    let cancelled = false;
    const authHeaders = () => ({ headers: { Authorization: `Bearer ${getStoredToken()}` } });
    const acquire = async () => {
      try {
        await axios.post(`${API}/api/intake/referrals/${fid}/acquire?session_id=${intakeSessionId}`, null, authHeaders());
        if (!cancelled) setLockConflict(null);
      } catch (err) {
        if (!cancelled && err?.response?.status === 409) {
          setLockConflict(err.response.data?.detail || {});
        }
        // Non-409 failures (network blip, table pending DDL) open normally —
        // the TTL machinery is the safety net.
      }
    };
    acquire();
    // Heartbeat every TTL/2 — idempotent for own session; skipped while
    // read-only (we don't hold the lock).
    const interval = setInterval(() => {
      if (!lockConflictRef.current) acquire();
    }, (LOCK_TTL_MINUTES / 2) * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
      // Release on close/switch — safe no-op if we never held it.
      axios.post(`${API}/api/intake/referrals/${fid}/release?session_id=${intakeSessionId}`, null, authHeaders()).catch(() => {});
    };
  }, [selectedReferral?.file_id, intakeSessionId]);

  // Best-effort release on tab close. sendBeacon can't carry the Bearer
  // header this API requires, so use fetch(keepalive) — survives unload AND
  // authenticates. TTL remains the real safety net.
  const lockedFileIdRef = useRef(null);
  lockedFileIdRef.current = selectedReferral && !lockConflict ? selectedReferral.file_id : null;
  useEffect(() => {
    const onBeforeUnload = () => {
      const fid = lockedFileIdRef.current;
      if (!fid) return;
      try {
        fetch(`${API}/api/intake/referrals/${fid}/release?session_id=${intakeSessionId}`, {
          method: "POST",
          keepalive: true,
          headers: { Authorization: `Bearer ${getStoredToken()}` },
        });
      } catch {
        // best-effort only
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [intakeSessionId]);

  const fetchData = useCallback(async (opts = {}) => {
    const { quiet = false } = opts;
    const token = getStoredToken();
    if (!token) return;
    
    if (!quiet) setLoading(true);
    try {
      // Branch the data source:
      //   - 'incomplete' → dedicated endpoint backed by Incomplete='Yes' in BQ
      //   - 'cancelled'  → existing /referrals?status_filter=cancelled
      //   - 'prior_auth' → existing /referrals?status_filter=prior_auth
      //                    (NeedsPriorAuth='Yes' in BQ, same row shape as
      //                    pending so full detail actions work)
      //   - 'pending'    → existing /referrals?status_filter=pending
      const isIncomplete = filter === "incomplete";
      const status_filter =
        filter === "cancelled" ? "cancelled"
        : filter === "prior_auth" ? "prior_auth"
        : "pending";

      const [refRes, statsRes] = await Promise.all([
        axios.get(
          isIncomplete
            ? `${API}/api/intake/incomplete`
            : `${API}/api/intake/referrals`,
          {
            params: isIncomplete
              ? { limit: 200, sort_dir: dateAddedSort }
              : filter === "cancelled"
              ? { status_filter, limit: 100, sort_dir: dateAddedSort, window: cancelledWindow }
              : { status_filter, limit: 100, sort_dir: dateAddedSort },
            headers: { Authorization: `Bearer ${token}` },
          }
        ),
        axios.get(`${API}/api/intake/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      const newReferrals = refRes.data.referrals;
      // Poll protection: merge fresh rows AROUND pending checkbox fields,
      // dropping any pending field the server now confirms.
      const pending = pendingTaskFieldsRef.current;
      const mergedReferrals = newReferrals.map((r) => {
        const pf = pending[r.file_id];
        if (!pf) return r;
        const confirmed = Object.keys(pf).every(
          (k) => (r.intake_status || {})[k] === pf[k]
        );
        if (confirmed) {
          setTaskFieldsPending(r.file_id, null);
          return r;
        }
        return { ...r, intake_status: { ...(r.intake_status || {}), ...pf } };
      });
      setReferrals(mergedReferrals);
      setStats(statsRes.data);
      if (filter === "cancelled") setCancelledMeta(refRes.data.window_counts || null);

      // If the user had a detail panel open and that referral has now
      // disappeared from the list (transferred, cancelled, or no longer
      // matches the current filter), close the panel so they aren't
      // looking at stale data.
      // The open panel also takes its row's checklist (server truth with any
      // pending optimistic fields merged in above), so dropping the overlay
      // once a step is confirmed can never untick it (2026-10-22 fix).
      setSelectedReferral((cur) => {
        if (!cur) return cur;
        const row = mergedReferrals.find((r) => r.file_id === cur.file_id);
        return row ? refreshFromRow(cur, row) : null;
      });
    } catch (err) {
      // Stay quiet during polling — surfacing a toast every 5s on a transient
      // network blip would be obnoxious. Hard errors still show on manual loads.
      if (!quiet) console.error("Failed to fetch referrals:", err);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [filter, dateAddedSort, cancelledWindow]);

  useEffect(() => {
    if (user) fetchData();
  }, [user, fetchData]);

  // 15-second background poll (P0 hotfix: was 5s — per-tab 5s polling of the
  // BQ-backed lists produced 11k+ slow requests/3h). fetchData only hits the
  // currently active tab's list; stats ride the same fetch. The CRM Queue's
  // own poll is untouched.
  // - Uses `quiet: true` so the loading spinner doesn't blink on each poll.
  // - List replacement is in-place via setReferrals; React preserves scroll.
  // - Detail panel stays open while its referral is still present.
  useEffect(() => {
    if (!user) return undefined;
    const id = setInterval(() => fetchData({ quiet: true }), 15000);
    return () => clearInterval(id);
  }, [user, fetchData]);

  // Filter referrals
  const filteredReferrals = referrals.filter((r) => {
    // 'cancelled' is enforced server-side via status_filter
    if (search) {
      const searchLower = search.toLowerCase();
      return (
        r.patient_name?.toLowerCase().includes(searchLower) ||
        r.insurance_carrier?.toLowerCase().includes(searchLower) ||
        r.employer_name?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  // Pill counts come from the /intake/stats endpoint which always queries the
  // FULL dataset — they must be independent of the currently-active filter.
  const pendingCount    = stats?.intake_pending ?? 0;
  const priorAuthCount  = stats?.prior_auth     ?? 0;
  const incompleteCount = stats?.incomplete     ?? 0;
  const cancelledCount  = stats?.cancelled      ?? 0;

  return (
    <div className={selectedReferral ? "p-4 h-full flex flex-col overflow-hidden" : "p-6 max-w-7xl mx-auto"}>
      {!selectedReferral && (
      <>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Referral Intake</h1>
          <p className="text-slate-500">
            New patients appear here first. Complete required tasks, then transfer to CRM Queue for scheduling.
          </p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 text-sm font-medium"
        >
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 gap-4 mb-6 max-w-2xl">
          <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="stat-card-pending">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <AlertCircle size={20} className="text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.intake_pending}</p>
                <p className="text-sm text-slate-500">Pending Intake</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="stat-card-cancelled">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-rose-100 flex items-center justify-center">
                <Ban size={20} className="text-rose-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.cancelled}</p>
                <p className="text-sm text-slate-500">Cancelled (last 14 days)</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4 mb-4">
        <div className="flex bg-slate-100 rounded-lg p-1">
          {[
            { key: "pending",    label: `Pending (${pendingCount})` },
            { key: "prior_auth", label: `Needs Prior Auth (${priorAuthCount})` },
            { key: "incomplete", label: `Incomplete (${incompleteCount})` },
            { key: "cancelled",  label: `Cancelled (${filter === "cancelled" && cancelledMeta ? cancelledMeta.shown : cancelledCount})` },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => {
                setFilter(f.key);
                // normal navigation resets the cancelled window to default
                if (f.key === "cancelled") setCancelledWindow("14d");
              }}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                filter === f.key
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
              data-testid={`filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search patients..."
            className="w-full h-9 pl-9 pr-4 rounded-lg border border-slate-200 text-sm"
          />
        </div>
      </div>
      {/* Cancelled tab is ALWAYS the last 14 days (product decision 2026-06:
          presets removed). The server window param survives ONLY for the
          Patient Search deep-link (window=all — no-dead-link rule); that
          state shows an inline history note instead of the removed control. */}
      {filter === "cancelled" && (
        <div className="flex items-center gap-3 mb-4" data-testid="cancelled-window-bar">
          {cancelledMeta && (
            <span className="text-xs text-slate-500" data-testid="cancelled-window-chip">
              showing {cancelledMeta.shown} of {cancelledMeta.total} total
            </span>
          )}
          {cancelledWindow === "all" && (
            <span
              className="text-xs text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-2 py-0.5"
              data-testid="cancelled-history-note"
            >
              showing full history for this search
            </span>
          )}
        </div>
      )}
      </>
      )}

      {/* Main Content — clicking a referral opens the split-screen
          workspace (documents left, squared patient detail right), covering
          the list. The app sidebar stays visible: this page only fills the
          routed content area right of it. */}
      {selectedReferral ? (
        <div className="flex-1 min-h-0 flex flex-col" data-testid="intake-workspace">
          <div className="flex items-center gap-3 mb-3 flex-shrink-0">
            <button
              onClick={() => setSelectedReferral(null)}
              data-testid="workspace-back-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <ChevronLeft size={16} /> Back to list
            </button>
            <p className="text-xs text-slate-400">Esc closes • documents left, patient details right</p>
          </div>
          {/* ~50/50 two-pane split. Below lg the split would be two
              unusably narrow panes — fall back to stacked panes instead. */}
          <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-2 gap-0 bg-white rounded-xl border border-slate-200 shadow-lg overflow-hidden">
            <div className="min-h-0 h-full overflow-hidden border-b lg:border-b-0 lg:border-r border-slate-200" data-testid="workspace-doc-pane">
              {/* keyed by file_id — switching patients swaps the WHOLE
                  workspace (auto-close-on-referral-switch semantics);
                  documents auto-load on open, no button press. */}
              <DocumentViewerPanel key={selectedReferral.file_id} fileId={selectedReferral.file_id} />
            </div>
            <div className="min-h-0 h-full overflow-hidden" data-testid="workspace-detail-pane">
              <ReferralDetail
                referral={selectedReferral}
                isIncompleteContext={filter === "incomplete"}
                isPendingContext={filter === "pending"}
                isPriorAuthContext={filter === "prior_auth"}
                isCancelledContext={filter === "cancelled"}
                taskOverlay={pendingTaskFields[selectedReferral.file_id] || null}
                onTaskPending={(fields) => setTaskFieldsPending(selectedReferral.file_id, fields)}
                readOnly={!!lockConflict}
                lockedBy={lockConflict?.locked_by_username}
                onUpdate={() => {
                  clearCardPrefetch(selectedReferral.file_id); // 2026-10-24: saves, cancel, uncancel and moves drop the cached card
                  fetchData();
                  // Refresh the selected referral. Merge, never replace, the
                  // checklist, and only into the referral still open
                  // (2026-10-22 fix).
                  const token = getStoredToken();
                  const fid = selectedReferral.file_id;
                  axios
                    .get(`${API}/api/intake/referrals/${fid}`, {
                      headers: { Authorization: `Bearer ${token}` },
                    })
                    .then((res) => {
                      setSelectedReferral((cur) =>
                        cur?.file_id === fid ? mergeDetail(cur, res.data) : cur
                      );
                    });
                }}
                onClose={() => setSelectedReferral(null)}
              />
            </div>
          </div>
        </div>
      ) : (
      <div className="flex gap-6">
        {/* Referral List */}
        <div className="flex-1">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 size={32} className="animate-spin text-blue-600" />
            </div>
          ) : filteredReferrals.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <FileCheck size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-600 font-medium">No referrals found</p>
              <p className="text-sm text-slate-400">
                {filter === "pending"
                  ? "All referrals have completed intake!"
                  : "No referrals match your search."}
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">
                      Patient
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">
                      Case Type
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">
                      Payor
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">
                      Intake Status
                    </th>
                    <th
                      onClick={() =>
                        setDateAddedSort((prev) => (prev === "desc" ? "asc" : "desc"))
                      }
                      className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap cursor-pointer select-none hover:text-slate-700"
                      data-testid="intake-date-added-header"
                      title="Sort by Date Added"
                    >
                      <span className="inline-flex items-center gap-1">
                        Date Added
                        {dateAddedSort === "desc" && <span aria-hidden="true">▼</span>}
                        {dateAddedSort === "asc" && <span aria-hidden="true">▲</span>}
                      </span>
                    </th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredReferrals.map((r) => (
                    <tr
                      key={r.file_id}
                      onClick={() => {
                        // save list scroll so Back/Esc restores position
                        listScrollRef.current = window.scrollY;
                        setSelectedReferral(r);
                      }}
                      className={`hover:bg-slate-50 cursor-pointer transition-colors ${
                        selectedReferral?.file_id === r.file_id ? "bg-blue-50" : ""
                      }`}
                      data-testid={`referral-row-${r.file_id}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <p className="font-medium text-slate-900">{r.patient_name}</p>
                          {/* One shared row template = the badge renders on
                              ALL four tabs (Pending / Needs Prior Auth /
                              Incomplete / Cancelled). */}
                          <LocationClassBadge cls={r.location_class} testId={`intake-row-locclass-${r.file_id}`} />
                          {r.is_locked && (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-700 whitespace-nowrap"
                              data-testid={`intake-row-lock-${r.file_id}`}
                              title={`Currently being worked by ${r.locked_by_username || "another rep"}`}
                            >
                              <Lock size={10} />
                              {r.locked_by_username || "locked"}
                            </span>
                          )}
                          {/* Dual-therapy pair member — strictly 'Yes' from
                              multiple_therapy_types_requested; renders on
                              all three tabs. */}
                          {r.dual_therapy && (
                            <span
                              className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-violet-100 text-violet-700 border border-violet-200 whitespace-nowrap"
                              data-testid={`intake-row-dual-${r.file_id}`}
                              title="Dual-therapy referral — this patient has a second case for the other therapy type."
                            >
                              Dual
                            </span>
                          )}
                          {r.has_notes && (
                            <span
                              className="inline-flex items-center px-1 py-0.5 rounded text-slate-400 bg-slate-100"
                              data-testid={`intake-row-notes-${r.file_id}`}
                              title="Has notes"
                            >
                              <StickyNote size={11} />
                            </span>
                          )}
                          {/* Incomplete-tab reason chip — muted, nothing on
                              NULL (legacy rows). Never crowds lock/notes. */}
                          {r.incomplete_reason && (
                            <span
                              className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-orange-50 text-orange-700 border border-orange-200 whitespace-nowrap"
                              data-testid={`intake-row-reason-${r.file_id}`}
                              title={`Incomplete: ${r.incomplete_reason}`}
                            >
                              {r.incomplete_reason}
                            </span>
                          )}
                          {/* Reconciliation gate summary — hidden at 0. */}
                          {typeof r.missing_count === "number" && r.missing_count > 0 && (
                            <span
                              className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-rose-50 text-rose-700 border border-rose-200 whitespace-nowrap"
                              data-testid={`intake-row-missing-${r.file_id}`}
                            >
                              {r.missing_count} missing
                            </span>
                          )}
                          {r.notes_preview && (
                            <span
                              className="text-[11px] text-slate-400 italic truncate max-w-[220px]"
                              data-testid={`intake-row-notes-preview-${r.file_id}`}
                              title={r.notes_preview}
                            >
                              {r.notes_preview}
                            </span>
                          )}
                          <span
                            className="text-[11px] text-slate-500 font-mono whitespace-nowrap"
                            data-testid={`intake-row-dob-${r.file_id}`}
                          >
                            DOB: {formatDOB(r.date_of_birth)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                            r.case_type === "WC"
                              ? "bg-blue-100 text-blue-700"
                              : "bg-purple-100 text-purple-700"
                          }`}
                        >
                          {r.case_type === "WC" ? (
                            <HardHat size={12} />
                          ) : (
                            <Car size={12} />
                          )}
                          {r.case_type}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-slate-900 truncate max-w-xs">
                          {r.insurance_carrier || "—"}
                        </p>
                        <p className="text-xs text-slate-500 truncate max-w-xs">
                          {r.employer_name || "—"}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {r.is_cancelled ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-700">
                              <Ban size={12} />
                              Cancelled
                            </span>
                          ) : (
                            <>
                              <StatusBadge
                                complete={r.intake_status?.billing_complete}
                                label="Billing"
                              />
                              {r.case_type === "WC" && (
                                <StatusBadge
                                  complete={r.intake_status?.payor_complete}
                                  label="Payor"
                                />
                              )}
                              <StatusBadge
                                complete={r.intake_status?.ready_for_crm}
                                label="Ready"
                              />
                            </>
                          )}
                        </div>
                      </td>
                      <td
                        className="px-4 py-3 text-xs text-slate-400 whitespace-nowrap"
                        data-testid="intake-date-added-cell"
                        title={r.created_at ? new Date(r.created_at).toLocaleString() : ""}
                      >
                        {r.created_at
                          ? new Date(r.created_at).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })
                          : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <ChevronRight size={16} className="text-slate-400" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
